import type {
	AdvancedMegaMenuHeaderRuntimePayload,
	AudienceMegaMenuHeaderRuntimePayload,
	ClassicHeaderRuntimePayload,
	MegaMenuHeaderRuntimePayload,
} from "@multi-tenant-cms/types/menu";
import React from "react";
import { beatThemeTypographyImportant } from "../theme/tenantThemeCss";
import {
	BUILDER_BUTTON_LINK_CLASS,
	ENTRY_META_AUTHOR_LINK_CLASS,
	resolveTypographyFontFamilyForCss,
} from "../theme/typographyTokens";
import type {
	ElementNode,
	ElementStyle,
	Responsive,
	TokenCategory,
	TokenRef,
} from "../types";
import { resolveLiveRateTablePublicYearLabel } from "../ui/global-variable/liveRateTableBlock";
import { renderMaterializedGlobalVariableRefsInText } from "../ui/global-variable/materializedRef";
import {
	ABOUT_AUTHOR_EDITOR_PLACEHOLDER,
	aboutAuthorPeopleFromEntryMeta,
	parseAboutAuthorConfigJson,
	parseAboutAuthorResolvedJson,
	pickAboutAuthorPerson,
} from "../ui/lib/aboutAuthorBinding";
import {
	hasContainerBackgroundLayers,
	parseContainerBackgroundSettings,
} from "../ui/lib/containerBackground";
import { resolveContainerTag } from "../ui/lib/containerTags";
import {
	parseEntriesGridConfigJson,
	parseEntriesGridItemsJson,
} from "../ui/lib/entriesGridBinding";
import {
	buildEntryMetaDisplaySegments,
	type EntryMetaPersonSegment,
	type EntryMetaResolved,
	parseEntryMetaResolvedJson,
} from "../ui/lib/entryMetaBinding";
import { cssWithoutGlobalBlockPlaceholder } from "../ui/lib/globalBlockPlaceholderStyle";
import { styleForPreservedLineBreaks } from "../ui/lib/inlineTextLineBreaks";
import {
	buildNavigationMenuLinkCss,
	isNavigationHrefCurrent,
	navMenuDataAttr,
	resolveNavMenuFlexDirection,
} from "../ui/lib/navigationMenuLinkStyles";
import {
	isDynamicTextBinding,
	resolveBoundTextContent,
	textBindingFromPersistedNode,
} from "../ui/lib/textElementBinding";
import {
	normalizeHeadingElementTag,
	normalizeTextElementTag,
} from "../ui/lib/textElementTag";
import type {
	Element,
	ElementStyles,
	EntryMetaElement,
	EntryMetaSeparatorStyle,
} from "../ui/types";
import {
	applyLiveRateTableVisibility,
	type LiveRateTableDisplaySnapshot,
} from "../utils/liveRateTableDisplay";
import {
	sanitizeSortableListItemHtml,
	sortableListItemNeedsHtml,
} from "../utils/sortableListItemHtml";
import {
	mergeSortableListSemanticStyles,
	sortableListItemMarkerStyle,
	sortableListItemTextStyle,
} from "../utils/sortableListSemanticStyles";
import { AdvancedMegaMenuHeader } from "./AdvancedMegaMenuHeader";
import { AudienceMegaMenuHeader } from "./AudienceMegaMenuHeader";
import { AboutAuthorCard } from "./aboutAuthorRuntime";
import { ClassicHeader } from "./ClassicHeader";
import { ContainerBackgroundLayers } from "./ContainerBackgroundLayers";
import {
	type CustomCodeFormSlot,
	CustomCodeFormSlots,
} from "./CustomCodeFormSlots";
import { CustomCodeHybrid } from "./CustomCodeHybrid";
import { CustomCodeRenderer } from "./CustomCodeRenderer";
import { CustomCodeSourcePreview } from "./CustomCodeSourcePreview";
import {
	clampColumnsWrapColumns,
	columnsAutoGridChildOuterStyle,
	columnsGapCssVar,
	wrappedColumnsRowChildOuterStyle,
} from "./columnsWrapFlexStyle";
import { CUSTOM_CODE_ROOT_ATTR } from "./customCodeCssScope";
import { CUSTOM_CODE_FORM_SLOT_PROP } from "./customCodeFormSlotRefs";
import {
	normalizeStaticHtmlForEmbed,
	resolveCustomCodeCompiledPayload,
} from "./customCodeHtmlRuntime";
import { DynamicPreviewImage, StaticPreviewImage } from "./DynamicPreviewImage";
import {
	deriveIconLinkAccessibleName,
	elementLinkOpensInNewTab,
	publishedLinkedElementAnchorStyle,
	trimmedElementLinkHref,
} from "./elementLink";
import { EntriesGridPublic } from "./entriesGridRuntime";
import { FaqAccordion } from "./FaqAccordion";
import { parseFaqDesignJson } from "./faqDesign";
import {
	BUILDER_AUTHOR_IMAGE_PLACEHOLDER_SRC,
	BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC,
	BUILDER_USER_IMAGE_PLACEHOLDER_SRC,
} from "./featuredImagePlaceholder";
import { flatElementStylesToReactCss } from "./flatElementStyles";
import { PublicFormConsent, PublicFormField } from "./formPublicField";
import { PublicFormRoot } from "./formPublicRuntime";
import { renderFormSubmitPublic } from "./formPublicShared";
import { mergeFullBleedLayoutStyle } from "./fullBleedLayout";
import { sanitizeIconifySvgMarkup } from "./iconifySvg";
import { sanitizeLocalAnimatedSvgMarkup } from "./localAnimatedSvg";
import {
	DEFAULT_LORDICON_JSON_SRC,
	formatLordiconColors,
	sanitizeLordiconSrc,
	sanitizeLordiconTrigger,
} from "./lordiconPublic";
import {
	resolveLucideAnimatedPlayOn,
	resolveLucideAnimatedPrimaryColor,
	resolveLucideAnimatedSecondaryColor,
	resolveLucideAnimatedStrokeWidth,
	sanitizeAnimationHostDomId,
} from "./lucideAnimatedPalette";
import { renderLucideAnimatedSsrFallback } from "./lucideAnimatedSsrFallback";
import { MegaMenuHeader } from "./MegaMenuHeader";
import {
	IMAGE_PLACEHOLDER_INTRINSIC,
	imageSizesAttributeFromLayout,
	imgElementIntrinsicProps,
} from "./mediaResponsive";
import { PanelSwitcher } from "./PanelSwitcher";
import {
	readPanelSwitcherTabAlign,
	readPanelSwitcherTabVariant,
} from "./panelSwitcherOptions";
import {
	plainTextFromHeadingHtml,
	resolveHeadingAnchorId,
	slugifyHeadingId,
} from "./slugifyHeadingId";
import { TableOfContents } from "./TableOfContents";
import { Video } from "./Video";
import { VisitorThemeSwitcherControl } from "./VisitorThemeSwitcherControl";

function parsePxInline(raw: unknown, fallback: number): number {
	if (typeof raw !== "string") {
		return fallback;
	}
	const n = Number.parseInt(raw.replace(/px/gi, "").trim(), 10);
	return Number.isFinite(n) && n > 0 ? n : fallback;
}

type CustomCodeProps = {
	tag?: "div";
	__builderCustomCode?: true;
	__codeType?: "html" | "react" | "nextjs";
	__sourceCode?: string;
	__cssCode?: string;
	__jsCode?: string;
	__compiledCode?: string;
	__componentId?: string;
	__compiledAt?: string;
	__validationStatus?: string;
	__validationMessages?: string[];
	__dependencies?: string[];
	__componentName?: string;
	__staticHtml?: string;
};

function tokenToValue(ref: unknown): string | undefined {
	if (!ref || typeof ref !== "object") {
		return undefined;
	}
	const o = ref as Record<string, unknown>;
	if (typeof o.value === "string") {
		return o.value;
	}
	if (typeof o.token === "string") {
		return o.token;
	}
	return undefined;
}

function pickResponsive<T>(value?: Responsive<T>): T | undefined {
	if (!value) {
		return undefined;
	}
	return value.desktop ?? value.tablet ?? value.mobile;
}

function pickResponsiveOrDirect<T>(value?: T | Responsive<T>): T | undefined {
	if (!value) {
		return undefined;
	}
	if (typeof value === "object" && value !== null) {
		const maybeResponsive = value as Responsive<T>;
		if (
			maybeResponsive.desktop !== undefined ||
			maybeResponsive.tablet !== undefined ||
			maybeResponsive.mobile !== undefined
		) {
			return pickResponsive(maybeResponsive);
		}
	}
	return value as T;
}

function tokenResponsiveToValue<TCategory extends TokenCategory>(
	value?: Responsive<TokenRef<TCategory>>
): string | undefined {
	const picked = pickResponsive(value);
	return tokenToValue(picked);
}

export function styleToInline(style?: ElementStyle): React.CSSProperties {
	const css: React.CSSProperties = {};
	if (!style) {
		return css;
	}
	// Typography string assignments below; beatThemeTypographyImportant sanitizes.

	const margin = pickResponsive(style.spacing?.margin);
	if (margin) {
		if (margin.top) {
			css.marginTop = tokenToValue(margin.top);
		}
		if (margin.right) {
			css.marginRight = tokenToValue(margin.right);
		}
		if (margin.bottom) {
			css.marginBottom = tokenToValue(margin.bottom);
		}
		if (margin.left) {
			css.marginLeft = tokenToValue(margin.left);
		}
	}

	const padding = pickResponsive(style.spacing?.padding);
	if (padding) {
		if (padding.top) {
			css.paddingTop = tokenToValue(padding.top);
		}
		if (padding.right) {
			css.paddingRight = tokenToValue(padding.right);
		}
		if (padding.bottom) {
			css.paddingBottom = tokenToValue(padding.bottom);
		}
		if (padding.left) {
			css.paddingLeft = tokenToValue(padding.left);
		}
	}

	const bgColor =
		style.background?.color && tokenToValue(style.background.color);
	if (bgColor) {
		css.backgroundColor = bgColor;
	}
	if (style.background?.gradient) {
		css.backgroundImage = style.background.gradient;
	}
	if (style.background?.size) {
		css.backgroundSize = style.background.size;
	}
	if (style.background?.repeat) {
		css.backgroundRepeat = style.background.repeat;
	}
	if (style.background?.position) {
		css.backgroundPosition = style.background.position;
	}

	const textColor =
		style.typography?.color && tokenToValue(style.typography.color);
	if (textColor) {
		css.color = textColor;
	}

	const fontFamilyRaw =
		style.typography?.fontFamily && tokenToValue(style.typography.fontFamily);
	if (fontFamilyRaw) {
		css.fontFamily = resolveTypographyFontFamilyForCss(fontFamilyRaw);
	}

	const fontSize = tokenResponsiveToValue(style.typography?.fontSize);
	if (fontSize) {
		css.fontSize = fontSize;
	}

	const fontWeight =
		style.typography?.fontWeight && tokenToValue(style.typography.fontWeight);
	if (fontWeight) {
		css.fontWeight = fontWeight as React.CSSProperties["fontWeight"];
	}

	const lineHeight =
		style.typography?.lineHeight && tokenToValue(style.typography.lineHeight);
	if (lineHeight) {
		css.lineHeight = lineHeight;
	}

	const letterSpacing =
		style.typography?.letterSpacing &&
		tokenToValue(style.typography.letterSpacing);
	if (letterSpacing) {
		css.letterSpacing = letterSpacing;
	}

	if (style.typography?.textTransform) {
		css.textTransform = style.typography.textTransform;
	}
	const textAlign = pickResponsive(style.typography?.textAlign);
	if (textAlign) {
		css.textAlign = textAlign;
	}

	if (style.typography?.fontStyle?.trim()) {
		css.fontStyle = style.typography.fontStyle.trim();
	}

	if (style.typography?.textDecoration?.trim()) {
		css.textDecoration = style.typography.textDecoration.trim();
	}

	if (style.typography?.backgroundClip?.trim()) {
		css.backgroundClip = style.typography.backgroundClip.trim();
	}

	if (style.typography?.webkitBackgroundClip?.trim()) {
		css.WebkitBackgroundClip = style.typography.webkitBackgroundClip.trim();
	}

	if (style.typography?.webkitTextFillColor?.trim()) {
		css.WebkitTextFillColor = style.typography.webkitTextFillColor.trim();
	}

	const width = pickResponsive(style.layout?.width);
	if (width) {
		css.width = width;
	}
	const height = pickResponsive(style.layout?.height);
	if (height) {
		css.height = height;
	}
	const minWidth = pickResponsive(style.layout?.minWidth);
	if (minWidth) {
		css.minWidth = minWidth;
	}
	const maxWidth = pickResponsive(style.layout?.maxWidth);
	if (maxWidth) {
		css.maxWidth = maxWidth;
	}
	const maxHeight = pickResponsive(style.layout?.maxHeight);
	if (maxHeight) {
		css.maxHeight = maxHeight;
	}
	const minHeight = pickResponsive(style.layout?.minHeight);
	if (minHeight) {
		css.minHeight = minHeight;
	}
	const objectFit = pickResponsive(style.layout?.objectFit);
	if (objectFit) {
		css.objectFit = objectFit;
	}
	const objectPosition = pickResponsive(style.layout?.objectPosition);
	if (objectPosition) {
		css.objectPosition = objectPosition;
	}

	if (style.layout?.display) {
		css.display = style.layout.display;
	}
	const float = pickResponsive(style.layout?.float);
	if (float) {
		css.float = float as React.CSSProperties["float"];
	}
	const position = pickResponsive(style.layout?.position);
	if (position) {
		css.position = position;
	}
	const zIndex = pickResponsive(style.layout?.zIndex);
	if (zIndex) {
		css.zIndex = zIndex;
	}
	const overflow = pickResponsive(style.layout?.overflow);
	if (overflow) {
		css.overflow = overflow;
	}
	const left = pickResponsive(style.layout?.left);
	if (left) {
		css.left = left;
	}
	const right = pickResponsive(style.layout?.right);
	if (right) {
		css.right = right;
	}
	const top = pickResponsive(style.layout?.top);
	if (top) {
		css.top = top;
	}
	const bottom = pickResponsive(style.layout?.bottom);
	if (bottom) {
		css.bottom = bottom;
	}
	const flexDirection = pickResponsive(style.layout?.flexDirection);
	if (flexDirection) {
		css.flexDirection = flexDirection;
	}
	const justifyContent = pickResponsive(style.layout?.justifyContent);
	if (justifyContent) {
		css.justifyContent = justifyContent;
	}
	const alignItems = pickResponsive(style.layout?.alignItems);
	if (alignItems) {
		css.alignItems = alignItems;
	}

	const gap = tokenResponsiveToValue(style.layout?.gap);
	if (gap) {
		css.gap = gap;
	}

	const borderRadius = tokenResponsiveToValue(style.border?.radius);
	if (borderRadius) {
		css.borderRadius = borderRadius;
	}
	const borderWidth = tokenResponsiveToValue(style.border?.width);
	if (borderWidth) {
		css.borderWidth = borderWidth;
	}
	const borderStyle = pickResponsive(style.border?.style);
	if (borderStyle) {
		css.borderStyle = borderStyle;
	}
	const borderColor = style.border?.color && tokenToValue(style.border.color);
	if (borderColor) {
		css.borderColor = borderColor;
	}

	const opacity = pickResponsive(style.effects?.opacity);
	if (typeof opacity === "number") {
		css.opacity = opacity;
	}
	const boxShadow = tokenResponsiveToValue(style.effects?.boxShadow);
	if (boxShadow) {
		css.boxShadow = boxShadow;
	}

	if (
		typeof style.effects?.animation === "string" &&
		style.effects.animation.trim()
	) {
		css.animation = style.effects.animation.trim();
	}
	if (
		typeof style.effects?.transition === "string" &&
		style.effects.transition.trim()
	) {
		css.transition = style.effects.transition.trim();
	}
	if (
		typeof style.effects?.transform === "string" &&
		style.effects.transform.trim()
	) {
		css.transform = style.effects.transform.trim();
	}

	return beatThemeTypographyImportant(
		css as Record<string, unknown>
	) as React.CSSProperties;
}

/**
 * The `form_embed` palette default (`elements.ts`) uses a dashed border as a
 * canvas “drop zone” hint. It is stored on the node and must not appear in
 * in-app preview or on the public site.
 */
function publicStyleForFormEmbed(
	css: React.CSSProperties
): React.CSSProperties {
	const out: React.CSSProperties = { ...css };
	if (
		typeof out.border === "string" &&
		/dashed/i.test(out.border) &&
		(/#94a3b8/i.test(out.border) ||
			/rgb\(\s*148\s*,\s*163\s*,\s*184\s*\)/i.test(out.border))
	) {
		delete out.border;
	}
	if (out.borderStyle === "dashed") {
		const c = out.borderColor;
		if (
			c === "#94a3b8" ||
			c === "rgb(148, 163, 184)" ||
			c === "rgba(148, 163, 184, 1)"
		) {
			delete out.border;
			delete out.borderWidth;
			delete out.borderStyle;
			delete out.borderColor;
		}
	}
	return out;
}

type WrapNode = (
	node: ElementNode,
	content: React.ReactNode
) => React.ReactNode;

/** Passed to `column` children for gap-aware flex basis (wrap + nowrap rows). */
export type ColumnsWrappedRowLayoutContext = {
	gap: string | undefined;
	columnCount: number;
	weightSum: number;
	/** `0` if parent row is `flex-wrap: wrap`, else `1` for `nowrap`. */
	flexItemShrink: 0 | 1;
	/**
	 * When set (≥2), children use equal-width auto-grid basis for this many
	 * columns per wrap row (ignores designed width fractions).
	 */
	wrapColumns?: number;
};

export type RenderOptions = {
	wrapNode?: WrapNode;
	/** Set while rendering inside an anchor container, so nested `<a>` degrades to `div`. */
	insideAnchor?: boolean;
	/**
	 * Page builder full-document preview: resolve `useFeaturedImage` blocks from
	 * Page settings featured URL, or placeholder when unset. Omit on the public
	 * site (featured `src` is injected server-side).
	 */
	featuredImagePreview?: boolean;
	/** Featured image URL when `featuredImagePreview` is true (may be empty). */
	featuredImageUrl?: string | null;
	/** Preview-only: resolves `imageBinding: author` when not already injected. */
	authorImageUrl?: string | null;
	/** Preview-only: resolves `imageBinding: user` when not already injected. */
	userImageUrl?: string | null;
	/** Resolved page strings for heading/text dynamic bindings (preview). */
	pageDynamicText?:
		| import("../ui/lib/textElementBinding").PageDynamicText
		| null;
	/** Entry `field_values` for `field:{slug}` text bindings (preview / SSR fallback). */
	fieldValues?: Record<string, unknown> | null;
	/** Resolved author/dates/read time for `entry_meta` blocks (preview when not injected on node). */
	entryMeta?: EntryMetaResolved | null;
	/** People list for `about_author` when not injected on the node. */
	aboutAuthorPeople?:
		| import("../ui/lib/aboutAuthorBinding").AboutAuthorPerson[]
		| null;
	aboutAuthorPrimaryId?: string | null;
	aboutAuthorReviewerId?: string | null;
	/**
	 * When true with `lcpImageNodeId`, the matching image node renders with
	 * `loading="eager"` and `fetchPriority="high"` (LCP hints on the public site).
	 */
	prioritizeFirstContentImage?: boolean;
	/** Builder node id of the first eligible image (see `findFirstLcpPriorityImageNodeId`). */
	lcpImageNodeId?: string;
	/**
	 * Shared across one document render so auto-generated heading `id`s stay unique
	 * (duplicate slugs break TOC list keys and scroll targets).
	 */
	usedHeadingIds?: Set<string>;
	/** Public site pathname for navigation menu `aria-current="page"`. */
	currentPathname?: string | null;
	/**
	 * Region SSR only: whether the `theme_switcher` element renders a real,
	 * working visitor Light|Dark toggle — set from Theme Design's "Allow
	 * visitors to switch themes" (`ThemeSiteDesign.colors.allowVisitorThemeSwitcher`).
	 * When `false`/unset, `theme_switcher` renders nothing on the public site,
	 * even if the element still exists in the page JSON (a stale placement
	 * from before the flag was turned off). The builder canvas render path
	 * never sets this — it has its own always-visible preview/disabled chrome
	 * for the element, independent of the visitor-facing flag, and its own
	 * separate toolbar Sun/Moon (`BuilderCanvasModeContext`) for canvas
	 * preview, which is a different concept entirely.
	 */
	visitorThemeSwitcherEnabled?: boolean;
	/**
	 * Audience mega menu: `mmh-audience` cookie from the request (Region SSR).
	 * Path hub still wins; cookie beats configured default on first paint.
	 */
	preferredAudienceId?: string | null;
	/** Template/thumbnail preview — placeholder TOC items only. */
	tableOfContentsPreviewStub?: boolean;
	/**
	 * Render "nothing here yet" placeholders for empty blocks. Opt-in, because these are
	 * authoring affordances: on a published page an empty custom-code block should occupy
	 * no space rather than show a visitor an editor prompt.
	 */
	emptyBlockPlaceholders?: boolean;
	/** Entry-edit preview — rescan headings when main content changes. */
	tableOfContentsWatchContent?: boolean;
	/** Entry page settings: AI-short TOC labels. */
	pageTocLabels?: {
		useAiTocLabels?: boolean;
		tocLabelOverrides?: Record<string, string>;
	};
	/** Entry builder: builder JSON fallback when headings live in custom_code iframes. */
	tocFallbackContentElements?: Element[];
	/**
	 * When rendering children of a public `form_root`, pass the root node id so
	 * field components can subscribe to validation errors across RSC boundaries.
	 */
	formValidationInstanceId?: string;
	/**
	 * When rendering a `column` inside a multi-column flex row, use gap-aware flex basis
	 * (see `wrappedColumnsRowChildOuterStyle`).
	 */
	columnsWrappedRow?: ColumnsWrappedRowLayoutContext;
	/**
	 * Builder device toolbar / layout catalog: force mega-menu compact mode and
	 * related chrome instead of reading `window.innerWidth`.
	 */
	forcedViewport?: "desktop" | "tablet" | "mobile" | null;
	/** Theme Builder / live preview: replica chrome must not collapse to the drawer stub. */
	previewMode?: boolean;
};

const IMAGE_MEDIA_UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type ImageNodePropsImg = {
	src?: string;
	mediaId?: string;
	useFeaturedImage?: boolean;
	imageBinding?: string;
	alt?: string;
	title?: string;
	caption?: string;
	showCaption?: boolean;
	lazyLoad?: boolean;
	openFullSizeOnClick?: boolean;
	href?: string;
	linkTarget?: "_blank" | "_self";
	objectFit?: string | Responsive<string>;
	objectPosition?: string | Responsive<string>;
	srcSet?: string;
	sizes?: string;
	slotVariantCssPx?: number;
	intrinsicWidth?: number;
	intrinsicHeight?: number;
};

type ImageNodeTopImg = {
	src?: string;
	mediaId?: string;
	alt?: string;
	link?: string;
	linkTarget?: "_blank" | "_self";
	caption?: string;
	showCaption?: boolean;
	lazyLoad?: boolean;
	openFullSizeOnClick?: boolean;
	title?: string;
};

function parseImageNodeProps(node: ElementNode): {
	topImg: ImageNodeTopImg;
	propsImg: ImageNodePropsImg;
} {
	const topImg = node as unknown as ImageNodeTopImg;
	const propsImg = (node.props as ImageNodePropsImg) || {};
	return { propsImg, topImg };
}

function resolvePublicImageIntrinsicProps(params: {
	propsImg: ImageNodePropsImg;
	imageSrc: string;
	binding: "static" | "featured" | "author" | "user";
}): { width?: number; height?: number } {
	const fromProps = imgElementIntrinsicProps(
		params.propsImg.intrinsicWidth,
		params.propsImg.intrinsicHeight
	);
	if (fromProps.width && fromProps.height) {
		return fromProps;
	}
	if (
		params.imageSrc === BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC ||
		params.imageSrc === BUILDER_AUTHOR_IMAGE_PLACEHOLDER_SRC ||
		params.imageSrc === BUILDER_USER_IMAGE_PLACEHOLDER_SRC
	) {
		return IMAGE_PLACEHOLDER_INTRINSIC;
	}
	if (
		(params.binding === "author" || params.binding === "user") &&
		params.imageSrc
	) {
		return { width: 96, height: 96 };
	}
	return {};
}

function resolveImageSrcFromImageProps(
	propsImg: ImageNodePropsImg,
	topImg: ImageNodeTopImg,
	options?: RenderOptions
): {
	imageSrc: string;
	binding: "static" | "featured" | "author" | "user";
	rawSrc: string;
	lazyPref: boolean | undefined;
} {
	const src = propsImg.src ?? topImg.src;
	const mediaId = propsImg.mediaId ?? topImg.mediaId;
	const rawSrc = typeof src === "string" ? src.trim() : "";
	const mid = typeof mediaId === "string" ? mediaId.trim() : "";
	const uuidLike = IMAGE_MEDIA_UUID_RE.test(mid);
	const legacyUrlInMediaId =
		mid && (mid.startsWith("/") || /^https?:\/\//i.test(mid)) ? mid : "";
	const binding = effectiveImageBindingFromProps(propsImg);
	let imageSrc =
		rawSrc ||
		legacyUrlInMediaId ||
		(uuidLike ? `/api/media/${mid}?content=1&variant=large` : "");
	if (options?.featuredImagePreview === true) {
		if (binding === "featured") {
			const u = options.featuredImageUrl?.trim();
			imageSrc = u || BUILDER_FEATURED_IMAGE_PLACEHOLDER_SRC;
		} else if (binding === "author") {
			const u = options.authorImageUrl?.trim();
			imageSrc = u || imageSrc || BUILDER_AUTHOR_IMAGE_PLACEHOLDER_SRC;
		} else if (binding === "user") {
			const u = options.userImageUrl?.trim();
			imageSrc = u || imageSrc || BUILDER_USER_IMAGE_PLACEHOLDER_SRC;
		}
	}
	const lazyPref = propsImg.lazyLoad ?? topImg.lazyLoad;
	return { binding, imageSrc, lazyPref, rawSrc };
}

/**
 * DFS: first image with a resolved non-empty `src` that is not explicitly lazy-loaded.
 * Used with `prioritizeFirstContentImage` + `lcpImageNodeId` for LCP hints.
 */
export function findFirstLcpPriorityImageNodeId(
	root: ElementNode[],
	options?: RenderOptions
): string | undefined {
	function walk(nodes: ElementNode[]): string | undefined {
		for (const n of nodes) {
			if (n.type === "image") {
				const { propsImg, topImg } = parseImageNodeProps(n);
				const { imageSrc, lazyPref } = resolveImageSrcFromImageProps(
					propsImg,
					topImg,
					options
				);
				if (imageSrc && lazyPref !== true) {
					return n.id;
				}
			}
			/* Raw `columns` can appear in layout bands though it is not in {@link ElementNode}. */
			const withChildren = n as { type?: string; children?: ElementNode[] };
			if (
				withChildren.type === "columns" &&
				Array.isArray(withChildren.children)
			) {
				for (const col of withChildren.children) {
					if (col && typeof col === "object" && "children" in col) {
						const colChildren = (col as { children?: ElementNode[] }).children;
						if (Array.isArray(colChildren)) {
							const hit = walk(colChildren);
							if (hit) {
								return hit;
							}
						}
					}
				}
			}
			if (Array.isArray(withChildren.children)) {
				const hit = walk(withChildren.children);
				if (hit) {
					return hit;
				}
			}
		}
		return undefined;
	}
	return walk(root);
}

function effectiveImageBindingFromProps(props: {
	imageBinding?: string;
	useFeaturedImage?: boolean;
}): "static" | "featured" | "author" | "user" {
	const b = props.imageBinding;
	if (b === "featured" || b === "author" || b === "user") {
		return b;
	}
	if (props.useFeaturedImage === true) {
		return "featured";
	}
	return "static";
}

function isActiveHref(href: string): boolean {
	const normalized = href.trim().toLowerCase();
	if (!normalized) {
		return false;
	}
	if (
		normalized === "#" ||
		normalized === "javascript:void(0)" ||
		normalized === "javascript:;"
	) {
		return false;
	}
	return true;
}

function resolveImageHeight(
	widthRaw: unknown,
	heightRaw: unknown
): string | undefined {
	if (!(typeof widthRaw === "string" && typeof heightRaw === "string")) {
		return typeof heightRaw === "string" ? heightRaw : undefined;
	}
	const w = widthRaw.trim().toLowerCase();
	const h = heightRaw.trim().toLowerCase();
	const wMatch = /^(-?\d+(?:\.\d+)?)px$/.exec(w);
	const hMatch = /^(-?\d+(?:\.\d+)?)%$/.exec(h);
	if (!(wMatch && hMatch)) {
		return heightRaw;
	}
	const wPx = Number.parseFloat(wMatch[1]);
	const hPct = Number.parseFloat(hMatch[1]);
	if (Number.isNaN(wPx) || Number.isNaN(hPct)) {
		return heightRaw;
	}
	return `${Math.max(0, Math.round((wPx * hPct) / 100))}px`;
}

/** Author-defined HTML id / classes on the published DOM (from builder settings). */
function publishedDomAttrs(node: ElementNode): {
	className?: string;
	id?: string;
	"data-pb-node": string;
} {
	const id = node.domId?.trim();
	const cls = node.domClass?.trim();
	const o: { className?: string; id?: string; "data-pb-node": string } = {
		"data-pb-node": node.id,
	};
	if (id) {
		o.id = id;
	}
	if (cls) {
		o.className = cls;
	}
	return o;
}

/** Runtime JSON may omit `id`; React list keys must be stable strings. */
function stableChildKey(
	parentId: string,
	childId: unknown,
	index: number
): string {
	if (typeof childId === "string" && childId.length > 0) {
		return childId;
	}
	return `${parentId}-idx-${index}`;
}

type EntryMetaRenderConfig = Pick<
	EntryMetaElement,
	| "authorLabel"
	| "showAuthor"
	| "showAuthorAvatar"
	| "showAuthorTitle"
	| "showReviewer"
	| "showReviewerAvatar"
	| "showReviewerTitle"
	| "reviewerLabel"
	| "reviewerBadge"
	| "linkAuthorProfile"
	| "linkReviewerProfile"
	| "showPublishedDate"
	| "showUpdatedDate"
	| "showReadTime"
	| "showSeparators"
	| "showTaxonomies"
	| "linkTaxonomyTerms"
	| "separatorStyle"
	| "publishedDatePrefix"
	| "updatedDatePrefix"
	| "readTimeSuffix"
	| "dateFormat"
	| "hideUpdatedWhenSameAsPublished"
	| "authorNameStyles"
	| "metaItemStyles"
	| "avatarSize"
	| "layoutDirection"
	| "showPublishedIcon"
	| "showUpdatedIcon"
	| "showReadTimeIcon"
>;

function parseEntryMetaConfigJson(raw: unknown): EntryMetaRenderConfig {
	if (typeof raw !== "string" || !raw.trim()) {
		return {};
	}
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			return parsed as EntryMetaRenderConfig;
		}
	} catch {
		/* ignore */
	}
	return {};
}

function entryMetaIconClock(): React.ReactNode {
	return (
		<svg
			aria-hidden
			fill="none"
			height={14}
			stroke="currentColor"
			strokeWidth={2}
			viewBox="0 0 24 24"
			width={14}
		>
			<title>Read time</title>
			<circle cx="12" cy="12" r="9" />
			<path d="M12 7v5l3 2" strokeLinecap="round" />
		</svg>
	);
}

function entryMetaIconCalendar(): React.ReactNode {
	return (
		<svg
			aria-hidden
			fill="none"
			height={14}
			stroke="currentColor"
			strokeWidth={2}
			viewBox="0 0 24 24"
			width={14}
		>
			<title>Date</title>
			<rect height="16" rx="2" width="18" x="3" y="5" />
			<path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
		</svg>
	);
}

function entryMetaSeparatorChar(
	style: EntryMetaSeparatorStyle | undefined
): string {
	return style === "pipe" ? "|" : "·";
}

/** React list keys must be unique even when ids/values repeat; include index deterministically. */
function stableListKey(prefix: string, raw: unknown, index: number): string {
	if (typeof raw === "string" && raw.length > 0) {
		return `${prefix}-${raw}-${index}`;
	}
	if (typeof raw === "number" && !Number.isNaN(raw)) {
		return `${prefix}-${raw}-${index}`;
	}
	return `${prefix}-idx-${index}`;
}

/**
 * Live editor elements (layout bands, etc.) store settings as flat `styles`.
 * Persisted entry docs use tokenized `style`. Merge so public render matches Canvas.
 * Re-exported from `flatElementStyles` for existing import sites.
 */
export { flatElementStylesToReactCss } from "./flatElementStyles";

type ResponsiveColumnsLayoutLike = {
	mobile?: {
		wrap?: boolean;
		wrapColumns?: number;
		gap?: string;
	};
};

function resolveMobileColumnsWrap(
	baseWrap: boolean,
	responsiveLayout: unknown
): boolean {
	if (!responsiveLayout || typeof responsiveLayout !== "object") {
		return baseWrap;
	}
	const mobileWrap = (responsiveLayout as ResponsiveColumnsLayoutLike).mobile
		?.wrap;
	return typeof mobileWrap === "boolean" ? mobileWrap : baseWrap;
}

function resolveMobileColumnsWrapColumns(
	baseWrapColumns: number | undefined,
	responsiveLayout: unknown
): number {
	if (responsiveLayout && typeof responsiveLayout === "object") {
		const mobileCols = (responsiveLayout as ResponsiveColumnsLayoutLike).mobile
			?.wrapColumns;
		if (typeof mobileCols === "number") {
			return clampColumnsWrapColumns(mobileCols);
		}
	}
	return clampColumnsWrapColumns(baseWrapColumns ?? 1);
}

function resolveMobileColumnsGap(
	baseGap: string | undefined,
	responsiveLayout: unknown
): string | undefined {
	if (responsiveLayout && typeof responsiveLayout === "object") {
		const mobileGap = (responsiveLayout as ResponsiveColumnsLayoutLike).mobile
			?.gap;
		if (typeof mobileGap === "string" && mobileGap.trim()) {
			return mobileGap;
		}
	}
	return baseGap;
}

/** DOM attrs + CSS vars for mobile wrap stack (1 col) or auto-grid (2+ cols). */
function columnsMobileWrapMarkers(args: {
	wrapOnMobile: boolean;
	wrapColumns: number;
	gap: string | undefined;
}): {
	attrs: Record<string, string>;
	cssVars: React.CSSProperties;
} {
	if (!args.wrapOnMobile) {
		return { attrs: {}, cssVars: {} };
	}
	const n = clampColumnsWrapColumns(args.wrapColumns);
	if (n <= 1) {
		return {
			attrs: { "data-pb-columns-stack-mobile": "1" },
			cssVars: {},
		};
	}
	const autoGrid = columnsAutoGridChildOuterStyle({
		wrapColumns: n,
		gap: args.gap,
	});
	const basis =
		typeof autoGrid.flexBasis === "string" ? autoGrid.flexBasis : "100%";
	return {
		attrs: { "data-pb-columns-wrap-cols": String(n) },
		cssVars: {
			["--pb-wrap-cols" as string]: String(n),
			["--pb-col-gap" as string]: columnsGapCssVar(args.gap),
			["--pb-col-basis" as string]: basis,
		},
	};
}

function mergedInlineStyle(node: ElementNode): React.CSSProperties {
	const rawStyles = (node as unknown as { styles?: Record<string, unknown> })
		.styles;
	const flat = flatElementStylesToReactCss(rawStyles);
	const token = styleToInline(node.style);
	return { ...flat, ...token };
}

/** Resolve heading/text dynamic bindings in preview when host supplies live entry data. */
function shouldResolvePreviewDynamicText(
	options: RenderOptions | undefined,
	binding: string | undefined
): boolean {
	if (!isDynamicTextBinding(binding)) {
		return false;
	}
	return (
		options?.featuredImagePreview === true ||
		options?.fieldValues != null ||
		options?.pageDynamicText != null
	);
}

function publishedHeaderChromeWrapStyle(
	style: React.CSSProperties
): React.CSSProperties {
	return { ...style, display: "contents" };
}

const HEADER_CHROME_PROP_FLAGS = [
	"__builderMegaMenuHeader",
	"__builderAdvancedMegaMenuHeader",
	"__builderAudienceMegaMenuHeader",
	"__builderClassicHeader",
	"__builderReplicaMegaMenuHeader",
] as const;

function isPublishedHeaderChromeProps(props: unknown): boolean {
	if (!props || typeof props !== "object") {
		return false;
	}
	const record = props as Record<string, unknown>;
	return HEADER_CHROME_PROP_FLAGS.some((key) => record[key] === true);
}

function childrenAreOnlyHeaderChrome(children: ElementNode[]): boolean {
	return (
		children.length > 0 &&
		children.every((child) => isPublishedHeaderChromeProps(child.props))
	);
}

/** Rendered published forms for `[data-cms-form]` placeholders (attached by Region). */
function collectRenderedCustomCodeFormSlots(
	node: ElementNode,
	options?: RenderOptions
): CustomCodeFormSlot[] {
	const slots: CustomCodeFormSlot[] = [];
	const children = "children" in node ? (node.children ?? []) : [];
	for (const child of children) {
		const ref = (child.props as Record<string, unknown> | undefined)?.[
			CUSTOM_CODE_FORM_SLOT_PROP
		];
		if (typeof ref === "string" && ref.length > 0) {
			slots.push({ ref, content: renderBuilderNode(child, options) });
		}
	}
	return slots;
}

export function renderBuilderNode(
	node: ElementNode,
	options?: RenderOptions
): React.ReactNode {
	const style = mergedInlineStyle(node);
	const dom = publishedDomAttrs(node);
	const wrap = options?.wrapNode;

	if (node.type === "container") {
		const containerProps = node.props ?? {};
		const props = containerProps as CustomCodeProps & {
			tag?: "section" | "div";
		};

		if (props.__builderCustomCode) {
			const codeType = props.__codeType || "html";
			const compiledPayload = resolveCustomCodeCompiledPayload(props);
			const hasCompiled = compiledPayload.length > 0;
			const hasSource =
				codeType === "html"
					? !!(props.__sourceCode || props.__cssCode || props.__jsCode)
					: !!props.__sourceCode;

			let content: React.ReactNode;
			const hasStaticHtml =
				typeof props.__staticHtml === "string" &&
				props.__staticHtml.trim().length > 0;

			if (hasStaticHtml && hasCompiled) {
				// Static HTML in the document for SEO / first paint; client upgrades to
				// CustomCodeRenderer so hooks, animations, and HTML-mode JS run.
				content = (
					<CustomCodeHybrid
						codeType={codeType}
						compiledCode={compiledPayload}
						componentId={props.__componentId || node.id}
						cssAudience="publish"
						fallbackStyles={style}
						key={node.id}
						staticHtml={props.__staticHtml!}
						wrapperClassName={dom.className}
						wrapperDataPbNode={node.id}
						wrapperId={dom.id}
					/>
				);
			} else if (hasStaticHtml) {
				// Snapshot only — no compiled payload to hydrate with.
				const customCodeRootId = props.__componentId || node.id;
				content = (
					<div
						{...dom}
						dangerouslySetInnerHTML={{
							__html: normalizeStaticHtmlForEmbed(
								props.__staticHtml!,
								customCodeRootId,
								"publish"
							),
						}}
						data-ssr="static"
						key={node.id}
						style={style}
						suppressHydrationWarning
						{...{ [CUSTOM_CODE_ROOT_ATTR]: customCodeRootId }}
					/>
				);
			} else if (hasCompiled) {
				// Fallback: render the component live on the client via CustomCodeRenderer
				content = (
					<CustomCodeRenderer
						codeType={codeType}
						compiledCode={compiledPayload}
						componentId={props.__componentId || node.id}
						cssAudience="publish"
						fallbackStyles={style}
						key={node.id}
						wrapperClassName={dom.className}
						wrapperDataPbNode={node.id}
						wrapperId={dom.id}
					/>
				);
			} else if (hasSource) {
				content = (
					<div {...dom} key={node.id} style={{ ...style, width: "100%" }}>
						<CustomCodeSourcePreview
							codeType={codeType}
							componentName={props.__componentName}
							cssCode={props.__cssCode}
							jsCode={props.__jsCode}
							minHeight={200}
							sourceCode={props.__sourceCode}
							style={{ width: "100%" }}
						/>
					</div>
				);
			} else if (options?.emptyBlockPlaceholders) {
				content = (
					<div
						{...dom}
						key={node.id}
						style={{
							...style,
							minHeight: 80,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "var(--theme-elevation-50, #f3f4f6)",
							borderRadius: 8,
							border: "1px dashed var(--theme-elevation-200, #e5e7eb)",
							color: "var(--theme-elevation-500, #6b7280)",
							fontSize: 13,
						}}
					>
						Custom code — compile or add source to preview
					</div>
				);
			} else {
				// Published page: no source and nothing compiled means nothing to show.
				// An editor prompt here would be visible to visitors.
				content = null;
			}
			const formSlots = collectRenderedCustomCodeFormSlots(node, options);
			if (content && formSlots.length > 0) {
				content = (
					<CustomCodeFormSlots key={node.id} slots={formSlots}>
						{content}
					</CustomCodeFormSlots>
				);
			}
			return wrap ? wrap(node, content) : content;
		}

		const navProps = containerProps as {
			__builderNavigationMenu?: boolean;
			__builderTableOfContents?: boolean;
			__tocTitle?: string;
			__tocContentSelector?: string;
			__tocExcludeSelector?: string;
			__tocLevelsJson?: string;
			__tocScrollOffset?: number;
			__tocSticky?: boolean;
			__tocNumbered?: boolean;
			__tocIndentStyle?: string;
			__tocSmoothScroll?: boolean;
			__tocHighlightActive?: boolean;
			__tocCollapseOnMobile?: boolean;
			__tocEmptyMessage?: string;
			__menuId?: string;
			__menuName?: string;
			__menuOrientation?: string;
			__navLinkColor?: string;
			__navLinkHoverColor?: string;
			__navLinkActiveColor?: string;
			__menuItems?: Array<{
				id?: string;
				label?: string;
				href?: string;
				openInNewTab?: boolean;
			}>;
		};
		if (
			(containerProps as { __builderMegaMenuHeader?: boolean })
				.__builderMegaMenuHeader
		) {
			const mhProps = containerProps as {
				__megaMenuPayloadJson?: string;
			};
			let payload: MegaMenuHeaderRuntimePayload | null = null;
			if (typeof mhProps.__megaMenuPayloadJson === "string") {
				try {
					payload = JSON.parse(
						mhProps.__megaMenuPayloadJson
					) as MegaMenuHeaderRuntimePayload;
				} catch {
					payload = null;
				}
			}
			if (!payload) {
				const content = (
					<div
						{...dom}
						key={node.id}
						style={{
							padding: 16,
							fontSize: 13,
							color: "#6b7280",
							...style,
						}}
					>
						Mega menu header — configure logo and menu in the inspector
					</div>
				);
				return wrap ? wrap(node, content) : content;
			}
			const content = (
				<div
					{...dom}
					key={node.id}
					style={publishedHeaderChromeWrapStyle(style)}
				>
					<MegaMenuHeader
						currentPathname={options?.currentPathname}
						forcedViewport={options?.forcedViewport}
						payload={payload}
						previewMode={options?.previewMode === true}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		if (
			(containerProps as { __builderAdvancedMegaMenuHeader?: boolean })
				.__builderAdvancedMegaMenuHeader
		) {
			const ammhProps = containerProps as {
				__megaMenuPayloadJson?: string;
			};
			let advancedPayload: AdvancedMegaMenuHeaderRuntimePayload | null = null;
			if (typeof ammhProps.__megaMenuPayloadJson === "string") {
				try {
					advancedPayload = JSON.parse(
						ammhProps.__megaMenuPayloadJson
					) as AdvancedMegaMenuHeaderRuntimePayload;
				} catch {
					advancedPayload = null;
				}
			}
			if (!advancedPayload) {
				const content = (
					<div
						{...dom}
						key={node.id}
						style={{
							padding: 16,
							fontSize: 13,
							color: "#6b7280",
							...style,
						}}
					>
						Advanced mega menu — configure logo and menu in the inspector
					</div>
				);
				return wrap ? wrap(node, content) : content;
			}
			const content = (
				<div
					{...dom}
					key={node.id}
					style={publishedHeaderChromeWrapStyle(style)}
				>
					<AdvancedMegaMenuHeader
						currentPathname={options?.currentPathname}
						forcedViewport={options?.forcedViewport}
						payload={advancedPayload}
						previewMode={options?.previewMode === true}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		if (
			(containerProps as { __builderAudienceMegaMenuHeader?: boolean })
				.__builderAudienceMegaMenuHeader
		) {
			const amhProps = containerProps as {
				__megaMenuPayloadJson?: string;
			};
			let audiencePayload: AudienceMegaMenuHeaderRuntimePayload | null = null;
			if (typeof amhProps.__megaMenuPayloadJson === "string") {
				try {
					audiencePayload = JSON.parse(
						amhProps.__megaMenuPayloadJson
					) as AudienceMegaMenuHeaderRuntimePayload;
				} catch {
					audiencePayload = null;
				}
			}
			if (!(audiencePayload && Array.isArray(audiencePayload.audiences))) {
				const content = (
					<div
						{...dom}
						key={node.id}
						style={{
							padding: 16,
							fontSize: 13,
							color: "#6b7280",
							...style,
						}}
					>
						Audience mega menu — configure audiences and menus in the inspector
					</div>
				);
				return wrap ? wrap(node, content) : content;
			}
			const content = (
				<div
					{...dom}
					key={node.id}
					style={publishedHeaderChromeWrapStyle(style)}
				>
					<AudienceMegaMenuHeader
						currentPathname={options?.currentPathname}
						forcedViewport={options?.forcedViewport}
						payload={audiencePayload}
						preferredAudienceId={options?.preferredAudienceId}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		if (
			(containerProps as { __builderClassicHeader?: boolean })
				.__builderClassicHeader
		) {
			const chProps = containerProps as {
				__classicHeaderPayloadJson?: string;
			};
			let chPayload: ClassicHeaderRuntimePayload | null = null;
			if (typeof chProps.__classicHeaderPayloadJson === "string") {
				try {
					chPayload = JSON.parse(
						chProps.__classicHeaderPayloadJson
					) as ClassicHeaderRuntimePayload;
				} catch {
					chPayload = null;
				}
			}
			if (!chPayload) {
				const content = (
					<div
						{...dom}
						key={node.id}
						style={{
							padding: 16,
							fontSize: 13,
							color: "#6b7280",
							...style,
						}}
					>
						Classic Header — configure logo and menu in the inspector
					</div>
				);
				return wrap ? wrap(node, content) : content;
			}
			const content = (
				<div
					{...dom}
					key={node.id}
					style={publishedHeaderChromeWrapStyle(style)}
				>
					<ClassicHeader payload={chPayload} />
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		if (navProps.__builderTableOfContents) {
			let levels: number[] | undefined;
			if (typeof navProps.__tocLevelsJson === "string") {
				try {
					const parsed = JSON.parse(navProps.__tocLevelsJson) as unknown;
					if (Array.isArray(parsed)) {
						levels = parsed.filter(
							(n): n is number => typeof n === "number" && n >= 1 && n <= 6
						);
					}
				} catch {
					levels = undefined;
				}
			}
			const indentRaw = navProps.__tocIndentStyle;
			const indentStyle =
				indentRaw === "indent" || indentRaw === "none" || indentRaw === "border"
					? indentRaw
					: undefined;
			const tocLabels = options?.pageTocLabels;
			const content = (
				<TableOfContents
					className={dom.className}
					collapseOnMobile={navProps.__tocCollapseOnMobile !== false}
					contentSelector={
						typeof navProps.__tocContentSelector === "string" &&
						navProps.__tocContentSelector.trim() !== ""
							? navProps.__tocContentSelector.trim()
							: undefined
					}
					dataPbNode={dom["data-pb-node"]}
					emptyMessage={
						typeof navProps.__tocEmptyMessage === "string" &&
						navProps.__tocEmptyMessage.trim() !== ""
							? navProps.__tocEmptyMessage.trim()
							: undefined
					}
					excludeSelector={
						typeof navProps.__tocExcludeSelector === "string" &&
						navProps.__tocExcludeSelector.trim() !== ""
							? navProps.__tocExcludeSelector.trim()
							: undefined
					}
					fallbackContentElements={options?.tocFallbackContentElements}
					highlightActive={navProps.__tocHighlightActive !== false}
					htmlId={dom.id}
					indentStyle={indentStyle}
					key={node.id}
					labelOverrides={tocLabels?.tocLabelOverrides}
					levels={levels}
					numbered={navProps.__tocNumbered === true}
					previewStub={options?.tableOfContentsPreviewStub === true}
					scrollOffset={
						typeof navProps.__tocScrollOffset === "number"
							? navProps.__tocScrollOffset
							: undefined
					}
					smoothScroll={navProps.__tocSmoothScroll !== false}
					sticky={navProps.__tocSticky !== false}
					style={style}
					title={
						typeof navProps.__tocTitle === "string"
							? navProps.__tocTitle
							: undefined
					}
					useAiTocLabels={tocLabels?.useAiTocLabels === true}
					watchContent={
						options?.tableOfContentsWatchContent === true ||
						options?.featuredImagePreview === true
					}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		if (navProps.__builderNavigationMenu) {
			const items = Array.isArray(navProps.__menuItems)
				? navProps.__menuItems
				: [];
			const flexDir = resolveNavMenuFlexDirection(
				typeof style.flexDirection === "string"
					? style.flexDirection
					: undefined,
				navProps.__menuOrientation === "vertical" ? "vertical" : "horizontal"
			);
			const navStyle: React.CSSProperties = {
				...style,
				color: style.color ?? "#111827",
				display: style.display ?? "flex",
				flexDirection: flexDir,
				flexWrap: style.flexWrap ?? (flexDir === "row" ? "wrap" : undefined),
				alignItems:
					style.alignItems ?? (flexDir === "row" ? "center" : "flex-start"),
				justifyContent: style.justifyContent,
				gap: style.gap ?? "0.75rem",
				fontFamily:
					style.fontFamily ??
					"var(--tt-font-sans, system-ui, -apple-system, Segoe UI, sans-serif)",
			};
			const linkCss = buildNavigationMenuLinkCss(node.id, {
				linkColor: navProps.__navLinkColor,
				linkHoverColor: navProps.__navLinkHoverColor,
				linkActiveColor: navProps.__navLinkActiveColor,
			});
			const resolvedLinkColor =
				typeof navProps.__navLinkColor === "string" &&
				navProps.__navLinkColor.trim() !== ""
					? navProps.__navLinkColor.trim()
					: typeof style.color === "string" && style.color.trim() !== ""
						? style.color
						: undefined;
			const content = (
				<>
					{linkCss ? (
						<style dangerouslySetInnerHTML={{ __html: linkCss }} />
					) : null}
					<nav
						{...navMenuDataAttr(node.id)}
						aria-label={navProps.__menuName || "Site menu"}
						{...dom}
						key={node.id}
						style={navStyle}
					>
						{items.length === 0 ? (
							<span style={{ fontSize: 13, opacity: 0.65 }}>No menu items</span>
						) : (
							<ul
								style={{
									display: "flex",
									flexDirection: "inherit",
									flexWrap: "inherit",
									gap: "inherit",
									margin: 0,
									padding: 0,
									listStyle: "none",
								}}
							>
								{items.map((it, i) => (
									<li key={stableListKey(`menu-item-${node.id}`, it.id, i)}>
										<a
											aria-current={
												isNavigationHrefCurrent(
													it.href,
													options?.currentPathname
												)
													? "page"
													: undefined
											}
											href={it.href || "#"}
											rel={it.openInNewTab ? "noopener noreferrer" : undefined}
											style={{
												color: resolvedLinkColor ?? "inherit",
												textDecoration: "none",
											}}
											target={it.openInNewTab ? "_blank" : undefined}
										>
											{it.label || "Link"}
										</a>
									</li>
								))}
							</ul>
						)}
					</nav>
				</>
			);
			return wrap ? wrap(node, content) : content;
		}

		const videoProps = containerProps as {
			__builderVideo?: boolean;
			__videoSrc?: string;
			__videoPosterSrc?: string;
			__videoControls?: boolean;
			__videoAutoplay?: boolean;
			__videoLoop?: boolean;
			__videoMuted?: boolean;
			__videoPlaysInline?: boolean;
			__videoPreload?: string;
			__videoAriaLabel?: string;
			__videoWidth?: number;
			__videoHeight?: number;
		};
		if (videoProps.__builderVideo) {
			const preload = videoProps.__videoPreload;
			const content = (
				<Video
					ariaLabel={videoProps.__videoAriaLabel}
					autoplay={videoProps.__videoAutoplay}
					controls={videoProps.__videoControls}
					domAttrs={dom}
					height={videoProps.__videoHeight}
					loop={videoProps.__videoLoop}
					muted={videoProps.__videoMuted}
					playsInline={videoProps.__videoPlaysInline}
					posterSrc={videoProps.__videoPosterSrc}
					preload={
						preload === "none" || preload === "auto" ? preload : "metadata"
					}
					src={videoProps.__videoSrc}
					style={style}
					width={videoProps.__videoWidth}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		const faqProps = containerProps as {
			__builderFaq?: boolean;
			__faqItemsJson?: string;
			__faqExpandMode?: string;
			__faqDefaultOpenFirst?: boolean;
			__faqTriggerIcon?: string;
			__faqIconPosition?: string;
			__faqSearchable?: boolean;
			__faqShowCategoryFilter?: boolean;
			__faqItemGap?: string;
			__faqQuestionTag?: string;
			__faqDefaultItemStylesJson?: string;
			__faqDefaultQuestionStylesJson?: string;
			__faqDefaultAnswerStylesJson?: string;
			__faqDefaultChipStylesJson?: string;
			__faqActiveChipStylesJson?: string;
			__faqDesignJson?: string;
		};
		if (faqProps.__builderFaq) {
			type FaqItemJson = {
				id?: string;
				question?: string;
				questionHtml?: boolean;
				answer?: string;
				answerHtml?: boolean;
				itemStyles?: Record<string, unknown>;
				questionStyles?: Record<string, unknown>;
				answerStyles?: Record<string, unknown>;
			};
			let items: FaqItemJson[] = [];
			const rawItems = faqProps.__faqItemsJson;
			if (typeof rawItems === "string" && rawItems.trim()) {
				try {
					const parsed = JSON.parse(rawItems) as unknown;
					if (Array.isArray(parsed)) {
						items = parsed as FaqItemJson[];
					}
				} catch {
					/* ignore */
				}
			}
			const parseFlat = (raw: string | undefined): Record<string, unknown> => {
				if (!(raw && raw.trim())) {
					return {};
				}
				try {
					const o = JSON.parse(raw) as unknown;
					if (o && typeof o === "object" && !Array.isArray(o)) {
						return o as Record<string, unknown>;
					}
				} catch {
					/* ignore */
				}
				return {};
			};
			const triggerIcon =
				faqProps.__faqTriggerIcon === "plus_minus"
					? "plus_minus"
					: faqProps.__faqTriggerIcon === "none"
						? "none"
						: "chevron";
			const { className: faqDomClass, ...faqDomRest } = dom;
			const content = (
				<FaqAccordion
					activeChipStyles={parseFlat(faqProps.__faqActiveChipStylesJson)}
					className={faqDomClass}
					defaultAnswerStyles={parseFlat(faqProps.__faqDefaultAnswerStylesJson)}
					defaultChipStyles={parseFlat(faqProps.__faqDefaultChipStylesJson)}
					defaultItemStyles={parseFlat(faqProps.__faqDefaultItemStylesJson)}
					defaultOpenFirst={faqProps.__faqDefaultOpenFirst === true}
					defaultQuestionStyles={parseFlat(
						faqProps.__faqDefaultQuestionStylesJson
					)}
					design={parseFaqDesignJson(faqProps.__faqDesignJson)}
					domProps={faqDomRest}
					expandMode={
						faqProps.__faqExpandMode === "single" ? "single" : "multiple"
					}
					iconPosition={
						faqProps.__faqIconPosition === "left" ? "left" : "right"
					}
					id={node.id}
					itemGap={
						typeof faqProps.__faqItemGap === "string" &&
						faqProps.__faqItemGap.trim()
							? faqProps.__faqItemGap.trim()
							: undefined
					}
					items={items}
					key={node.id}
					questionTag={faqProps.__faqQuestionTag ?? "h3"}
					searchable={faqProps.__faqSearchable === true}
					showCategoryFilter={faqProps.__faqShowCategoryFilter === true}
					style={style}
					triggerIcon={triggerIcon}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		const entryMetaProps = containerProps as {
			__builderEntryMeta?: boolean;
			__entryMetaConfigJson?: string;
			__entryMetaResolvedJson?: string;
		};
		if (entryMetaProps.__builderEntryMeta) {
			const config = parseEntryMetaConfigJson(
				entryMetaProps.__entryMetaConfigJson
			);
			const resolved =
				parseEntryMetaResolvedJson(entryMetaProps.__entryMetaResolvedJson) ??
				options?.entryMeta ??
				null;
			const avatarPx = parsePxInline(config.avatarSize, 24);
			const authorNameStyle = flatElementStylesToReactCss(
				(config.authorNameStyles ?? {}) as Record<string, unknown>
			);
			const metaItemStyle = flatElementStylesToReactCss(
				(config.metaItemStyles ?? {}) as Record<string, unknown>
			);
			const metaRowStyle: React.CSSProperties = {
				display: "inline-flex",
				alignItems: "center",
				gap: "0.35em",
				...metaItemStyle,
			};
			const { className: entryDomClass, ...entryDomRest } = dom;
			const rootClass = ["pb-entry-meta", entryDomClass]
				.filter(Boolean)
				.join(" ");
			const outerStyle: React.CSSProperties = {
				...style,
				display: style.display ?? "flex",
				flexDirection:
					config.layoutDirection === "column"
						? "column"
						: (style.flexDirection ?? "row"),
				flexWrap: style.flexWrap ?? "wrap",
				alignItems: style.alignItems ?? "center",
				gap: style.gap ?? "8px",
			};
			const sepChar = entryMetaSeparatorChar(config.separatorStyle);
			const showSep = config.showSeparators !== false;
			const personChipStyle: React.CSSProperties = {
				display: "inline-flex",
				alignItems: "center",
				gap: "0.5em",
				...authorNameStyle,
			};
			const personLinkStyle: React.CSSProperties = {
				...personChipStyle,
				color: "inherit",
				textDecoration: "none",
				cursor: "pointer",
			};
			const { people, terms, texts } = resolved
				? buildEntryMetaDisplaySegments(config, resolved)
				: { people: [], terms: [], texts: [] };
			const renderPersonChip = (person: EntryMetaPersonSegment) => {
				const imageSrc =
					person.imageUrl?.trim() ||
					(options?.featuredImagePreview === true
						? BUILDER_AUTHOR_IMAGE_PLACEHOLDER_SRC
						: "");
				const avatar =
					person.showAvatar && imageSrc ? (
						<img
							alt=""
							height={avatarPx}
							src={imageSrc}
							style={{
								borderRadius: "50%",
								objectFit: "cover",
								flexShrink: 0,
							}}
							width={avatarPx}
						/>
					) : null;
				const identity = (
					<>
						{avatar}
						<span>{person.name}</span>
					</>
				);
				const href = person.href.trim();
				const external = /^https?:\/\//i.test(href);
				const linked = href ? (
					<a
						className={ENTRY_META_AUTHOR_LINK_CLASS}
						href={href}
						rel={external ? "noopener noreferrer" : undefined}
						style={personLinkStyle}
						target={external ? "_blank" : undefined}
					>
						{identity}
					</a>
				) : (
					<span style={personChipStyle}>{identity}</span>
				);
				return (
					<span key={person.key} style={personChipStyle}>
						{person.label ? <span>{person.label}</span> : null}
						{linked}
					</span>
				);
			};
			const textIcon = (key: "read" | "published" | "updated") => {
				if (key === "read") {
					return config.showReadTimeIcon === false
						? null
						: entryMetaIconClock();
				}
				if (key === "published" && config.showPublishedIcon === false) {
					return null;
				}
				if (key === "updated" && config.showUpdatedIcon === false) {
					return null;
				}
				return entryMetaIconCalendar();
			};
			const termChipStyle: React.CSSProperties = {
				display: "inline-flex",
				alignItems: "center",
				padding: "0.15em 0.7em",
				borderRadius: "999px",
				background: "color-mix(in srgb, currentColor 8%, transparent)",
				fontSize: "0.85em",
				fontWeight: 500,
				...metaItemStyle,
			};
			const segments: React.ReactNode[] = [
				...people.map((person) => renderPersonChip(person)),
				...texts.map((item) => (
					<span key={item.key} style={metaRowStyle}>
						{textIcon(item.key)}
						<span>{item.text}</span>
					</span>
				)),
				...terms.map((term) =>
					term.href ? (
						<a
							className={ENTRY_META_AUTHOR_LINK_CLASS}
							href={term.href}
							key={term.key}
							style={{
								...termChipStyle,
								color: "inherit",
								textDecoration: "none",
							}}
						>
							{term.name}
						</a>
					) : (
						<span key={term.key} style={termChipStyle}>
							{term.name}
						</span>
					)
				),
			];

			const content = (
				<div
					{...entryDomRest}
					className={rootClass}
					key={node.id}
					style={outerStyle}
				>
					{segments.map((segment, i) => (
						<React.Fragment
							key={stableListKey(`entry-meta-${node.id}-seg`, i, i)}
						>
							{i > 0 && showSep ? (
								<span aria-hidden="true">{sepChar}</span>
							) : null}
							{segment}
						</React.Fragment>
					))}
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const aboutAuthorProps = containerProps as {
			__builderAboutAuthor?: boolean;
			__aboutAuthorConfigJson?: string;
			__aboutAuthorResolvedJson?: string;
		};
		if (aboutAuthorProps.__builderAboutAuthor) {
			const config = parseAboutAuthorConfigJson(
				aboutAuthorProps.__aboutAuthorConfigJson
			);
			const people =
				options?.aboutAuthorPeople ??
				(options?.entryMeta
					? aboutAuthorPeopleFromEntryMeta(options.entryMeta)
					: []);
			const person =
				parseAboutAuthorResolvedJson(
					aboutAuthorProps.__aboutAuthorResolvedJson
				) ??
				pickAboutAuthorPerson(
					config,
					people,
					options?.aboutAuthorPrimaryId,
					options?.aboutAuthorReviewerId
				) ??
				(options?.featuredImagePreview === true
					? ABOUT_AUTHOR_EDITOR_PLACEHOLDER
					: null);
			const { className: aboutDomClass, ...aboutDomRest } = dom;
			const aboutClass = ["pb-about-author", aboutDomClass]
				.filter(Boolean)
				.join(" ");
			if (!person) {
				return wrap ? wrap(node, null) : null;
			}
			const content = (
				<div
					{...aboutDomRest}
					className={aboutClass}
					key={node.id}
					style={style}
				>
					<AboutAuthorCard
						config={config}
						person={person}
						placeholderImageSrc={
							options?.featuredImagePreview === true
								? BUILDER_AUTHOR_IMAGE_PLACEHOLDER_SRC
								: undefined
						}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const themeSwitcherProps = containerProps as {
			__builderThemeSwitcher?: boolean;
			__themeSwitcherAriaLabel?: string;
			__themeSwitcherVariant?: string;
			__themeSwitcherSize?: string;
			__themeSwitcherDisplayMode?: string;
			__themeSwitcherIconColor?: string;
			__themeSwitcherBorderColor?: string;
			__themeSwitcherActiveBackground?: string;
		};
		if (themeSwitcherProps.__builderThemeSwitcher) {
			// Public site: render nothing when visitors can't switch — even for
			// an existing element placement left over from before the flag was
			// turned off (see `RenderOptions.visitorThemeSwitcherEnabled`).
			if (!options?.visitorThemeSwitcherEnabled) {
				return null;
			}
			const displayMode =
				themeSwitcherProps.__themeSwitcherDisplayMode === "hide" ||
				themeSwitcherProps.__themeSwitcherDisplayMode === "disabled" ||
				themeSwitcherProps.__themeSwitcherDisplayMode === "show"
					? themeSwitcherProps.__themeSwitcherDisplayMode
					: "show";
			if (displayMode === "hide") {
				return null;
			}
			const appearance =
				themeSwitcherProps.__themeSwitcherVariant === "icon"
					? "icon"
					: "segmented";
			const size =
				themeSwitcherProps.__themeSwitcherSize === "sm" ||
				themeSwitcherProps.__themeSwitcherSize === "lg" ||
				themeSwitcherProps.__themeSwitcherSize === "md"
					? themeSwitcherProps.__themeSwitcherSize
					: "md";
			const interaction = displayMode === "disabled" ? "disabled" : "live";
			const content = (
				<div {...dom} key={node.id} style={style}>
					<VisitorThemeSwitcherControl
						activeBackground={
							themeSwitcherProps.__themeSwitcherActiveBackground
						}
						ariaLabel={themeSwitcherProps.__themeSwitcherAriaLabel}
						borderColor={themeSwitcherProps.__themeSwitcherBorderColor}
						disabledReason="instance"
						iconColor={themeSwitcherProps.__themeSwitcherIconColor}
						interaction={interaction}
						size={size}
						variant={appearance}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const entriesGridProps = containerProps as {
			__builderEntriesGrid?: boolean;
			__entriesGridConfigJson?: string;
			__entriesGridItemsJson?: string;
		};
		if (entriesGridProps.__builderEntriesGrid) {
			const config = parseEntriesGridConfigJson(
				entriesGridProps.__entriesGridConfigJson
			);
			const items =
				parseEntriesGridItemsJson(entriesGridProps.__entriesGridItemsJson) ??
				[];
			const { className: egDomClass, ...egDomRest } = dom;
			const rootClass = ["pb-entries-grid", egDomClass]
				.filter(Boolean)
				.join(" ");
			const content = (
				<div
					{...egDomRest}
					className={rootClass || undefined}
					key={node.id}
					style={style}
				>
					<EntriesGridPublic
						autoStack
						config={config}
						items={items}
						preview={Boolean(options?.featuredImagePreview)}
					/>
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const sortableProps = containerProps as {
			__builderSortableList?: boolean;
			__sortableVariant?: string;
			__sortableMarkerColor?: string;
			__sortableMarkerOpacity?: number;
			__sortableMarkerStyle?: string;
			__sortableItemsJson?: string;
		};
		if (sortableProps.__builderSortableList) {
			let items: { id?: string; text?: string }[] = [];
			const raw = sortableProps.__sortableItemsJson;
			if (typeof raw === "string" && raw.trim()) {
				try {
					const parsed = JSON.parse(raw) as unknown;
					if (Array.isArray(parsed)) {
						items = parsed as { id?: string; text?: string }[];
					}
				} catch {
					/* ignore */
				}
			}
			const listVariant =
				sortableProps.__sortableVariant === "ol" ? "ol" : "ul";
			const ListTag = listVariant === "ol" ? "ol" : "ul";
			const listStyle = mergeSortableListSemanticStyles(
				style,
				listVariant,
				sortableProps.__sortableMarkerStyle
			);
			const itemTextStyle = sortableListItemTextStyle(style);
			const itemMarkerStyle = sortableListItemMarkerStyle(
				sortableProps.__sortableMarkerColor,
				sortableProps.__sortableMarkerOpacity
			);
			const content = (
				<ListTag {...dom} key={node.id} style={listStyle}>
					{items.map((it, i) => {
						const itemText = typeof it.text === "string" ? it.text : "";
						const asHtml = sortableListItemNeedsHtml(itemText);
						return (
							<li
								key={stableListKey(`sortable-${node.id}`, it.id, i)}
								style={itemMarkerStyle}
							>
								<span
									{...(asHtml
										? {
												dangerouslySetInnerHTML: {
													__html: sanitizeSortableListItemHtml(itemText),
												},
											}
										: {})}
									style={{
										...itemTextStyle,
										...(asHtml ? {} : styleForPreservedLineBreaks(itemText)),
									}}
								>
									{asHtml ? undefined : itemText}
								</span>
							</li>
						);
					})}
				</ListTag>
			);
			return wrap ? wrap(node, content) : content;
		}

		const panelSwitcher = containerProps as {
			__builderPanelSwitcher?: true;
			__panelSwitcherOrientation?: string;
			__panelSwitcherPanelGap?: string;
			__panelSwitcherTabAlign?: string;
			__panelSwitcherTabGap?: string;
			__panelSwitcherTabStylesJson?: string;
			__panelSwitcherTabVariant?: string;
			__panelSwitcherActiveTabStylesJson?: string;
		};
		if (panelSwitcher.__builderPanelSwitcher) {
			const panelNodes = node.children || [];
			// Same shape as the FAQ branch's `parseFlat` — kept local to match this file.
			const parsePanelStyles = (raw: string | undefined) => {
				if (!(raw && raw.trim())) {
					return;
				}
				try {
					const o = JSON.parse(raw) as unknown;
					if (o && typeof o === "object" && !Array.isArray(o)) {
						return o as ElementStyles;
					}
				} catch {
					/* ignore */
				}
				return;
			};
			const content = (
				<PanelSwitcher
					activeTabStyles={parsePanelStyles(
						panelSwitcher.__panelSwitcherActiveTabStylesJson
					)}
					domProps={dom}
					id={node.id}
					key={node.id}
					orientation={
						panelSwitcher.__panelSwitcherOrientation === "vertical"
							? "vertical"
							: "horizontal"
					}
					panelGap={panelSwitcher.__panelSwitcherPanelGap}
					panels={panelNodes.map((child, childIndex) => ({
						id: child.id,
						label:
							typeof (child.props as { __builderPanelLabel?: unknown })
								?.__builderPanelLabel === "string"
								? ((child.props as { __builderPanelLabel: string })
										.__builderPanelLabel as string)
								: `Tab ${childIndex + 1}`,
						content: renderBuilderNode(child, options),
					}))}
					style={style}
					tabAlign={readPanelSwitcherTabAlign(
						panelSwitcher.__panelSwitcherTabAlign
					)}
					tabGap={panelSwitcher.__panelSwitcherTabGap}
					tabStyles={parsePanelStyles(
						panelSwitcher.__panelSwitcherTabStylesJson
					)}
					tabVariant={readPanelSwitcherTabVariant(
						panelSwitcher.__panelSwitcherTabVariant
					)}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		const formEmbed = containerProps as {
			__builderFormEmbed?: true;
			__formEmbedId?: string;
			__formEmbedMissing?: boolean;
			__formEmbedError?: string | null;
		};
		if (formEmbed.__builderFormEmbed) {
			const ch = node.children || [];
			if (
				ch.length === 0 &&
				(formEmbed.__formEmbedMissing || formEmbed.__formEmbedError)
			) {
				const content = (
					<div
						{...dom}
						key={node.id}
						style={{
							...style,
							fontSize: 14,
							color: "var(--theme-elevation-500, #6b7280)",
							border: "1px dashed var(--theme-elevation-200, #e5e7eb)",
							borderRadius: 8,
							padding: 12,
						}}
					>
						{formEmbed.__formEmbedError === "invalid_form_schema"
							? "This form is misconfigured and cannot be displayed."
							: "This form is not available. It may be unpublished or was removed."}
					</div>
				);
				return wrap ? wrap(node, content) : content;
			}
			const content = (
				<div {...dom} key={node.id} style={publicStyleForFormEmbed(style)}>
					{ch.map((child, childIndex) => (
						<React.Fragment key={stableChildKey(node.id, child.id, childIndex)}>
							{renderBuilderNode(child, options)}
						</React.Fragment>
					))}
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const liveRateTableProps = containerProps as {
			__builderLiveRateTable?: true;
			__liveRateTableJson?: string;
		};
		if (liveRateTableProps.__builderLiveRateTable) {
			type LivePayload = {
				hiddenColumnKeys?: string[];
				hiddenRowIds?: number[];
				liveCaption?: string;
				rateSetKey?: string;
				rateSetName?: string;
				tableSnapshot?: {
					bands?: Array<{
						displaySn?: number;
						label?: string;
						rowId?: number;
						values?: Record<string, string>;
					}>;
					columns?: Array<{ key?: string; name?: string }>;
					taxYear?: string;
					yearLabels?: string[];
				} | null;
				yearChips?: string[];
			};
			let payload: LivePayload = {};
			if (
				typeof liveRateTableProps.__liveRateTableJson === "string" &&
				liveRateTableProps.__liveRateTableJson.trim()
			) {
				try {
					payload = JSON.parse(
						liveRateTableProps.__liveRateTableJson
					) as LivePayload;
				} catch {
					payload = {};
				}
			}
			const snapshotRaw = payload.tableSnapshot;
			const normalizedSnapshot = snapshotRaw
				? (() => {
						const columns = (snapshotRaw.columns ?? []).flatMap((column) =>
							typeof column.key === "string" && column.key.trim().length > 0
								? [{ key: column.key, name: column.name }]
								: []
						);
						const bands = snapshotRaw.bands ?? [];
						if (columns.length === 0 || bands.length === 0) {
							return null;
						}
						return {
							bands,
							columns,
							taxYear: snapshotRaw.taxYear,
						} satisfies LiveRateTableDisplaySnapshot;
					})()
				: null;
			const snapshot = normalizedSnapshot
				? applyLiveRateTableVisibility(normalizedSnapshot, {
						hiddenColumnKeys: payload.hiddenColumnKeys,
						hiddenRowIds: payload.hiddenRowIds,
					})
				: null;
			const taxYear =
				snapshot?.taxYear?.trim() || snapshotRaw?.taxYear?.trim() || "";
			const publicYearLabel = resolveLiveRateTablePublicYearLabel({
				tableSnapshot: taxYear ? { taxYear } : null,
				yearChips: payload.yearChips,
			});
			const publicYearBadges = publicYearLabel ? [publicYearLabel] : [];
			const hasTableData =
				(snapshot?.columns?.length ?? 0) > 0 &&
				(snapshot?.bands?.length ?? 0) > 0;
			const content = (
				<div {...dom} key={node.id} style={style}>
					<div
						key={`${node.id}-lrt-header`}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 8,
							marginBottom: 8,
						}}
					>
						<div style={{ fontSize: 14, fontWeight: 600 }}>
							{payload.rateSetName || payload.rateSetKey || "Global Tables"}
						</div>
						{publicYearBadges.length > 0 ? (
							<div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
								{publicYearBadges.map((chip, chipIdx) => (
									<span
										key={stableListKey(`${node.id}-lrt-chip`, chip, chipIdx)}
										style={{
											padding: "2px 8px",
											borderRadius: 999,
											fontSize: 11,
											background: "rgba(34,211,238,0.28)",
											border: "1px solid rgba(34,211,238,0.55)",
										}}
									>
										{chip}
									</span>
								))}
							</div>
						) : null}
					</div>
					{payload.liveCaption ? (
						<div
							key={`${node.id}-lrt-caption`}
							style={{ fontSize: 12, marginBottom: 8, opacity: 0.8 }}
						>
							{payload.liveCaption}
						</div>
					) : null}
					{hasTableData ? (
						<div key={`${node.id}-lrt-table`} style={{ overflowX: "auto" }}>
							<table style={{ width: "100%", borderCollapse: "collapse" }}>
								<thead>
									<tr>
										<th style={{ textAlign: "left", padding: 6 }}>S.N.</th>
										<th style={{ textAlign: "left", padding: 6 }}>Band</th>
										{snapshot?.columns?.map((column, idx) => (
											<th
												key={stableListKey(
													`${node.id}-lrt-col`,
													column.key,
													idx
												)}
												style={{ textAlign: "left", padding: 6 }}
											>
												{column.name || column.key || "Column"}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{snapshot?.bands?.map((band, rowIdx) => (
										<tr
											key={stableListKey(
												`${node.id}-lrt-row`,
												band.rowId,
												rowIdx
											)}
										>
											<td style={{ padding: 6 }}>
												{band.displaySn ?? rowIdx + 1}
											</td>
											<td style={{ padding: 6 }}>{band.label || "Band"}</td>
											{snapshot.columns?.map((column, colIdx) => (
												<td
													key={stableListKey(
														`${node.id}-lrt-cell-${rowIdx}`,
														column.key,
														colIdx
													)}
													style={{ padding: 6 }}
												>
													{(column.key && band.values?.[column.key]) || "—"}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div
							key={`${node.id}-lrt-empty`}
							style={{ fontSize: 12, opacity: 0.7 }}
						>
							Global Tables are not configured.
						</div>
					)}
				</div>
			);
			return wrap ? wrap(node, content) : content;
		}

		const fRoot = containerProps as {
			__builderFormRoot?: true;
			__formActionUrl?: string;
			__formMethod?: string;
			__formSuccessMessage?: string;
			__formDbId?: string;
			__formSlug?: string;
			__formSubmitPath?: string;
			__formSchemaJson?: string;
			__formDesignJson?: string;
			__formRecaptchaEnabled?: boolean;
			__formRecaptchaSiteKey?: string;
			__builderScopedCss?: string;
		};
		if (fRoot.__builderFormRoot) {
			const scopedFormCss =
				typeof fRoot.__builderScopedCss === "string" &&
				fRoot.__builderScopedCss.trim()
					? fRoot.__builderScopedCss
					: undefined;
			const ch = node.children || [];
			const formFieldOptions: RenderOptions = {
				...options,
				formValidationInstanceId: node.id,
			};
			const formInner = ch.map((child, childIndex) => (
				<React.Fragment key={stableChildKey(node.id, child.id, childIndex)}>
					{renderBuilderNode(child, formFieldOptions)}
				</React.Fragment>
			));
			const inner = (
				<React.Fragment key={node.id}>
					{scopedFormCss ? (
						<style
							dangerouslySetInnerHTML={{ __html: scopedFormCss }}
							key={`${node.id}-pb-form-scoped-css`}
						/>
					) : null}
					<PublicFormRoot
						actionUrl={fRoot.__formActionUrl}
						formDbId={
							typeof fRoot.__formDbId === "string"
								? fRoot.__formDbId
								: undefined
						}
						formDesignJson={
							typeof fRoot.__formDesignJson === "string"
								? fRoot.__formDesignJson
								: undefined
						}
						formSchemaJson={
							typeof fRoot.__formSchemaJson === "string"
								? fRoot.__formSchemaJson
								: undefined
						}
						formSlug={
							typeof fRoot.__formSlug === "string"
								? fRoot.__formSlug
								: undefined
						}
						formSubmitPath={
							typeof fRoot.__formSubmitPath === "string"
								? fRoot.__formSubmitPath
								: undefined
						}
						htmlClassName={dom.className}
						htmlId={dom.id}
						key={node.id}
						method={fRoot.__formMethod}
						nodeId={node.id}
						recaptchaEnabled={fRoot.__formRecaptchaEnabled}
						recaptchaSiteKey={fRoot.__formRecaptchaSiteKey}
						style={style}
						successMessageClient={fRoot.__formSuccessMessage}
					>
						{formInner}
					</PublicFormRoot>
				</React.Fragment>
			);
			return wrap ? wrap(node, inner) : inner;
		}

		const fField = containerProps as { __builderFormField?: true };
		if (fField.__builderFormField) {
			const content = (
				<PublicFormField
					formInstanceId={options?.formValidationInstanceId}
					id={node.id}
					props={containerProps as never}
					style={style}
					wrapperDom={dom}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		const fSub = containerProps as {
			__builderFormSubmit?: true;
			__fsContent?: string;
		};
		if (fSub.__builderFormSubmit) {
			const content = renderFormSubmitPublic(node.id, fSub, style, dom);
			return wrap ? wrap(node, content) : content;
		}

		const fCons = containerProps as {
			__builderFormConsent?: true;
			__fcName?: string;
			__fcLabel?: string;
			__fcRequired?: boolean;
			__fcLabelStylesJson?: string;
			__fcInputStylesJson?: string;
		};
		if (fCons.__builderFormConsent) {
			const content = (
				<PublicFormConsent
					dom={dom}
					formInstanceId={options?.formValidationInstanceId}
					id={node.id}
					props={fCons}
					style={style}
				/>
			);
			return wrap ? wrap(node, content) : content;
		}

		const plainProps = containerProps as {
			tag?: string;
			__builderContainerTag?: string;
			__builderContainerHref?: string;
			__builderContainerTarget?: string;
			__builderOwnTypography?: boolean;
			__builderScopedCss?: string;
			__builderContentWidth?: string;
			__builderContainerBackground?: unknown;
		};
		const liveContainerBackground = (node as { background?: unknown })
			.background;
		const containerBackground = parseContainerBackgroundSettings(
			plainProps.__builderContainerBackground ?? liveContainerBackground
		);
		const containerHasLayers =
			hasContainerBackgroundLayers(containerBackground);
		const scopedCss =
			typeof plainProps.__builderScopedCss === "string" &&
			plainProps.__builderScopedCss.trim()
				? plainProps.__builderScopedCss
				: undefined;
		// Serialisers narrow `tag` to div/section and carry the real one in
		// __builderContainerTag; without reading it, <article> etc. publish as <div>.
		const requestedTag = resolveContainerTag(
			plainProps.__builderContainerTag ?? plainProps.tag
		);
		const anchorHref =
			typeof plainProps.__builderContainerHref === "string" &&
			plainProps.__builderContainerHref.trim()
				? plainProps.__builderContainerHref.trim()
				: undefined;
		// Nested <a> is invalid HTML and browsers silently restructure it, so an
		// anchor container inside another anchor degrades to a plain div.
		const renderAsAnchor =
			requestedTag === "a" && !!anchorHref && !options?.insideAnchor;
		const Tag =
			requestedTag === "a" ? (renderAsAnchor ? "a" : "div") : requestedTag;
		// Hands typography for this subtree to page / site CSS — see OWN_TYPOGRAPHY_EXCLUDE.
		const ownTypographyAttrs = plainProps.__builderOwnTypography
			? { "data-pb-own-type": "" }
			: {};
		const anchorAttrs = renderAsAnchor
			? {
					href: anchorHref,
					...(plainProps.__builderContainerTarget === "_blank"
						? { target: "_blank", rel: "noopener noreferrer" }
						: plainProps.__builderContainerTarget === "_self"
							? { target: "_self" }
							: {}),
				}
			: {};
		const children = node.children || [];
		const columnsMeta = containerProps as {
			__builderColumns?: number;
			__builderColumnsWrap?: boolean;
			__builderColumnsWrapColumns?: number;
			__builderColumnsGap?: string;
			__builderResponsiveColumnsLayout?: unknown;
		};
		const wrapFlag = columnsMeta.__builderColumnsWrap;
		const wrapFromToken =
			wrapFlag === true || (wrapFlag !== false && style.flexWrap === "wrap");
		const wrapOnMobile = resolveMobileColumnsWrap(
			wrapFromToken,
			columnsMeta.__builderResponsiveColumnsLayout
		);
		const mobileWrapColumns = resolveMobileColumnsWrapColumns(
			columnsMeta.__builderColumnsWrapColumns,
			columnsMeta.__builderResponsiveColumnsLayout
		);
		const mobileWrapGap = resolveMobileColumnsGap(
			columnsMeta.__builderColumnsGap,
			columnsMeta.__builderResponsiveColumnsLayout
		);
		const isBuilderColumnsRow =
			typeof columnsMeta.__builderColumns === "number" &&
			columnsMeta.__builderColumns > 0;
		const mobileWrapMarkers = columnsMobileWrapMarkers({
			wrapOnMobile: isBuilderColumnsRow && wrapOnMobile,
			wrapColumns: mobileWrapColumns,
			gap: mobileWrapGap,
		});
		const desktopWrapColumns = clampColumnsWrapColumns(
			columnsMeta.__builderColumnsWrapColumns ?? 1
		);
		const useDesktopAutoGrid = wrapFromToken && desktopWrapColumns > 1;
		const columnsWrappedRowOpts: ColumnsWrappedRowLayoutContext | undefined =
			isBuilderColumnsRow && children.length > 1
				? {
						gap: columnsMeta.__builderColumnsGap,
						columnCount: useDesktopAutoGrid
							? desktopWrapColumns
							: Math.max(1, columnsMeta.__builderColumns ?? children.length),
						weightSum: useDesktopAutoGrid
							? desktopWrapColumns
							: (() => {
									let sum = 0;
									for (const ch of children) {
										const wf = (
											ch.props as { widthFraction?: number } | undefined
										)?.widthFraction;
										sum += typeof wf === "number" && wf > 0 ? wf : 1;
									}
									return sum > 0 ? sum : 1;
								})(),
						flexItemShrink: wrapFromToken ? 0 : 1,
						...(useDesktopAutoGrid ? { wrapColumns: desktopWrapColumns } : {}),
					}
				: undefined;
		const tagStyle = mergeFullBleedLayoutStyle(
			cssWithoutGlobalBlockPlaceholder(style),
			plainProps.__builderContentWidth === "fullViewport"
		);
		const headerChromePassthrough = childrenAreOnlyHeaderChrome(children);
		const flattenHeaderChromeAncestor =
			headerChromePassthrough && !containerHasLayers && !scopedCss;
		const tagStyleWithLayers: React.CSSProperties = flattenHeaderChromeAncestor
			? { display: "contents" }
			: containerHasLayers
				? {
						...tagStyle,
						...mobileWrapMarkers.cssVars,
						...(useDesktopAutoGrid ||
						Object.keys(mobileWrapMarkers.attrs).length > 0
							? {
									boxSizing: "border-box" as const,
									width: tagStyle.width ?? "100%",
									maxWidth: tagStyle.maxWidth ?? "100%",
								}
							: {}),
						position: tagStyle.position ?? "relative",
						overflow: headerChromePassthrough
							? "visible"
							: (tagStyle.overflow ?? "hidden"),
					}
				: {
						...tagStyle,
						...mobileWrapMarkers.cssVars,
						...(useDesktopAutoGrid ||
						Object.keys(mobileWrapMarkers.attrs).length > 0
							? {
									boxSizing: "border-box" as const,
									width: tagStyle.width ?? "100%",
									maxWidth: tagStyle.maxWidth ?? "100%",
								}
							: {}),
						...(headerChromePassthrough ? { overflow: "visible" } : {}),
					};
		const childNodes = children.map((child, childIndex) => (
			<React.Fragment key={stableChildKey(node.id, child.id, childIndex)}>
				{renderBuilderNode(child, {
					...options,
					columnsWrappedRow: columnsWrappedRowOpts,
					...(renderAsAnchor ? { insideAnchor: true } : {}),
				})}
			</React.Fragment>
		));
		const content = (
			<React.Fragment key={node.id}>
				{scopedCss ? (
					<style
						dangerouslySetInnerHTML={{ __html: scopedCss }}
						key={`${node.id}-pb-container-scoped-css`}
					/>
				) : null}
				<Tag
					{...dom}
					{...mobileWrapMarkers.attrs}
					{...anchorAttrs}
					{...ownTypographyAttrs}
					key={`${node.id}-pb-container-body`}
					style={tagStyleWithLayers}
				>
					{containerHasLayers ? (
						<ContainerBackgroundLayers
							background={containerBackground}
							inlineLayersOnly
						>
							{childNodes}
						</ContainerBackgroundLayers>
					) : (
						childNodes
					)}
				</Tag>
			</React.Fragment>
		);
		return wrap ? wrap(node, content) : content;
	}

	/**
	 * Editor `columns` nodes are normalized to `container`+`__builderColumns` when
	 * saving entries, but layout top/bottom bands persist raw `Element[]` — render
	 * them here or the subtree disappears on the public site.
	 */
	if ((node as { type?: string }).type === "columns") {
		const colRoot = node as unknown as {
			id: string;
			gap?: string;
			alignItems?: "flex-start" | "center" | "flex-end" | "stretch";
			justifyContent?:
				| "flex-start"
				| "center"
				| "flex-end"
				| "space-between"
				| "space-around";
			wrap?: boolean;
			wrapColumns?: number;
			responsiveColumnsLayout?: unknown;
			children?: ElementNode[];
		};
		const mobileWrap = resolveMobileColumnsWrap(
			Boolean(colRoot.wrap),
			colRoot.responsiveColumnsLayout
		);
		const mobileWrapColumns = resolveMobileColumnsWrapColumns(
			colRoot.wrapColumns,
			colRoot.responsiveColumnsLayout
		);
		const mobileWrapGap = resolveMobileColumnsGap(
			typeof colRoot.gap === "string" ? colRoot.gap : undefined,
			colRoot.responsiveColumnsLayout
		);
		const mobileWrapMarkers = columnsMobileWrapMarkers({
			wrapOnMobile: mobileWrap,
			wrapColumns: mobileWrapColumns,
			gap: mobileWrapGap,
		});
		const desktopWrapColumns = clampColumnsWrapColumns(
			colRoot.wrapColumns ?? 1
		);
		const useDesktopAutoGrid = Boolean(colRoot.wrap) && desktopWrapColumns > 1;
		const flexStyle: React.CSSProperties = {
			display: "flex",
			flexDirection: "row",
			flexWrap: colRoot.wrap ? "wrap" : "nowrap",
			...style,
			...mobileWrapMarkers.cssVars,
			...(useDesktopAutoGrid || Object.keys(mobileWrapMarkers.attrs).length > 0
				? {
						boxSizing: "border-box" as const,
						width: style.width ?? "100%",
						maxWidth: style.maxWidth ?? "100%",
					}
				: {}),
			gap:
				style.gap ??
				(typeof colRoot.gap === "string" ? colRoot.gap : undefined) ??
				"24px",
			alignItems: style.alignItems ?? colRoot.alignItems ?? "stretch",
			justifyContent:
				style.justifyContent ?? colRoot.justifyContent ?? "flex-start",
		};
		const ch = colRoot.children ?? [];
		const columnsWrappedRowOpts: ColumnsWrappedRowLayoutContext | undefined =
			ch.length > 1
				? {
						gap:
							(typeof colRoot.gap === "string" ? colRoot.gap : undefined) ??
							"24px",
						columnCount: useDesktopAutoGrid
							? desktopWrapColumns
							: Math.max(1, ch.length),
						weightSum: useDesktopAutoGrid
							? desktopWrapColumns
							: (() => {
									let sum = 0;
									for (const c of ch) {
										const wf = (
											c.props as { widthFraction?: number } | undefined
										)?.widthFraction;
										sum += typeof wf === "number" && wf > 0 ? wf : 1;
									}
									return sum > 0 ? sum : 1;
								})(),
						flexItemShrink: colRoot.wrap ? 0 : 1,
						...(useDesktopAutoGrid ? { wrapColumns: desktopWrapColumns } : {}),
					}
				: undefined;
		const content = (
			<div
				{...dom}
				{...mobileWrapMarkers.attrs}
				key={node.id}
				style={flexStyle}
			>
				{ch.map((child, childIndex) => (
					<React.Fragment key={stableChildKey(node.id, child.id, childIndex)}>
						{renderBuilderNode(child, {
							...options,
							columnsWrappedRow: columnsWrappedRowOpts,
						})}
					</React.Fragment>
				))}
			</div>
		);
		return wrap ? wrap(node, content) : content;
	}

	if (node.type === "column") {
		const topCol = node as unknown as {
			widthFraction?: number;
			settings?: {
				align?: "left" | "center" | "right";
				alignVertical?: "top" | "center" | "bottom";
				widthType?: "auto" | "percent";
				widthPercent?: number;
			};
		};
		const widthFraction =
			(node.props as { widthFraction?: number } | undefined)?.widthFraction ??
			topCol.widthFraction;
		const colSettings =
			(
				node.props as {
					__columnSettings?: {
						align?: "left" | "center" | "right";
						alignVertical?: "top" | "center" | "bottom";
						widthType?: "auto" | "percent";
						widthPercent?: number;
					};
				}
			)?.__columnSettings ?? topCol.settings;
		const align = colSettings?.align ?? "left";
		const alignItems: React.CSSProperties["alignItems"] =
			align === "center"
				? "center"
				: align === "right"
					? "flex-end"
					: "flex-start";
		const alignV = colSettings?.alignVertical ?? "top";
		const justifyContent: React.CSSProperties["justifyContent"] =
			alignV === "center"
				? "center"
				: alignV === "bottom"
					? "flex-end"
					: "flex-start";
		const rowCtx = options?.columnsWrappedRow;
		const autoGridCols =
			rowCtx?.wrapColumns == null
				? 0
				: clampColumnsWrapColumns(rowCtx.wrapColumns);
		const outerFlex: React.CSSProperties = rowCtx
			? autoGridCols > 1
				? columnsAutoGridChildOuterStyle({
						wrapColumns: autoGridCols,
						gap: rowCtx.gap,
					})
				: wrappedColumnsRowChildOuterStyle({
						widthFraction:
							typeof widthFraction === "number" ? widthFraction : 1,
						weightSum: rowCtx.weightSum,
						columnCount: rowCtx.columnCount,
						gap: rowCtx.gap,
						flexItemShrink: rowCtx.flexItemShrink,
						widthPercent:
							colSettings?.widthType === "percent"
								? colSettings.widthPercent
								: null,
					})
			: {
					flex: typeof widthFraction === "number" ? widthFraction : 1,
				};
		const content = (
			<div
				{...dom}
				key={node.id}
				style={{
					display: "flex",
					flexDirection: "column",
					...style,
					...outerFlex,
					alignItems,
					justifyContent,
				}}
			>
				{(node.children || []).map((child, childIndex) => (
					<React.Fragment key={stableChildKey(node.id, child.id, childIndex)}>
						{renderBuilderNode(child, {
							...options,
							columnsWrappedRow: undefined,
						})}
					</React.Fragment>
				))}
			</div>
		);
		return wrap ? wrap(node, content) : content;
	}

	if (node.type === "heading") {
		const props = (node.props ?? {}) as {
			content?: string;
			html?: boolean;
			tag?: string;
			textBinding?: string;
		};
		const staticBody = props.content ?? "";
		const headingBinding = textBindingFromPersistedNode(
			node as { textBinding?: string },
			props
		);
		const body = renderMaterializedGlobalVariableRefsInText(
			shouldResolvePreviewDynamicText(options, headingBinding)
				? resolveBoundTextContent(
						{
							type: "heading",
							content: staticBody,
							...(headingBinding && isDynamicTextBinding(headingBinding)
								? { textBinding: headingBinding }
								: {}),
						},
						options!.pageDynamicText,
						{
							forEditor: false,
							fieldValues: options!.fieldValues,
						}
					)
				: staticBody
		);
		const level = normalizeHeadingElementTag(props.tag);
		const html = props.html;
		const headingDom = { ...dom };
		const usedHeadingIds = options?.usedHeadingIds;
		const plain = html ? plainTextFromHeadingHtml(body) : body;
		if (plain.trim()) {
			if (usedHeadingIds) {
				headingDom.id = resolveHeadingAnchorId({
					domId: headingDom.id,
					text: plain,
					used: usedHeadingIds,
				});
			} else if (!headingDom.id?.trim()) {
				headingDom.id = slugifyHeadingId(plain);
			}
		}
		const heading = html
			? React.createElement(level, {
					...headingDom,
					dangerouslySetInnerHTML: { __html: body },
					key: node.id,
					style,
				})
			: React.createElement(
					level,
					{
						...headingDom,
						key: node.id,
						style: {
							...style,
							...styleForPreservedLineBreaks(body),
						},
					},
					body
				);
		return wrap ? wrap(node, heading) : heading;
	}

	if (node.type === "text") {
		const topTxt = node as unknown as { content?: string; tag?: string };
		const props =
			(node.props as {
				content?: string;
				html?: boolean;
				tag?: string;
				textBinding?: string;
			}) || {};
		const staticBody = props.content ?? topTxt.content ?? "";
		const textBinding = textBindingFromPersistedNode(
			node as { textBinding?: string },
			props
		);
		const textBody = renderMaterializedGlobalVariableRefsInText(
			shouldResolvePreviewDynamicText(options, textBinding)
				? resolveBoundTextContent(
						{
							type: "text",
							content: staticBody,
							...(textBinding && isDynamicTextBinding(textBinding)
								? { textBinding }
								: {}),
						},
						options!.pageDynamicText,
						{
							forEditor: false,
							fieldValues: options!.fieldValues,
						}
					)
				: staticBody
		);
		const html = props.html;
		const Tag = normalizeTextElementTag(props.tag ?? topTxt.tag);
		const text = html
			? React.createElement(Tag, {
					...dom,
					dangerouslySetInnerHTML: { __html: textBody },
					key: node.id,
					style,
				})
			: React.createElement(
					Tag,
					{
						...dom,
						key: node.id,
						style: {
							...style,
							...styleForPreservedLineBreaks(textBody),
						},
					},
					textBody
				);
		return wrap ? wrap(node, text) : text;
	}

	if (node.type === "button") {
		const topBtn = node as unknown as {
			content?: string;
			link?: string;
			target?: string;
			openLightboxId?: string;
		};
		const propsBtn =
			(node.props as {
				label?: string;
				href?: string;
				openLightboxId?: string;
				openInNewTab?: boolean;
				buttonIconKind?: string;
				iconLeftSvgMarkup?: string;
				iconLeftAriaLabel?: string;
				iconRightSvgMarkup?: string;
				iconRightAriaLabel?: string;
				iconPlacement?: string;
				iconLucideMode?: string;
				iconLucideLibraryId?: string;
				iconLucideLocalSvgMarkup?: string;
				iconLucideAriaLabel?: string;
				iconLucidePrimaryColor?: string;
				iconLucideSecondaryColor?: string;
				iconLucideStrokeWidth?: string;
				iconLucidePlayOn?: string;
				iconLucideAnimationHostDomId?: string;
				iconLucideSizePx?: number;
			}) || {};
		const label = propsBtn.label ?? topBtn.content;
		const href = propsBtn.href ?? topBtn.link;
		const openLightboxId =
			(typeof propsBtn.openLightboxId === "string" &&
				propsBtn.openLightboxId.trim()) ||
			(typeof topBtn.openLightboxId === "string" &&
				topBtn.openLightboxId.trim()) ||
			"";
		const openInNewTab = propsBtn.openInNewTab ?? topBtn.target === "_blank";
		const isLightboxTrigger = openLightboxId.length > 0;
		// A linked button inside an anchor container would nest <a> in <a>, which is
		// invalid and silently restructured by browsers — render a <button> instead.
		const isLink =
			!(isLightboxTrigger || options?.insideAnchor) &&
			typeof href === "string" &&
			href.length > 0;
		const alignment = style.textAlign as
			| "left"
			| "center"
			| "right"
			| "justify"
			| undefined;
		const justifyMap: Record<
			"left" | "center" | "right" | "justify",
			React.CSSProperties["justifyContent"]
		> = {
			left: "flex-start",
			center: "center",
			right: "flex-end",
			justify: "space-between",
		};
		const rawLeft =
			typeof propsBtn.iconLeftSvgMarkup === "string"
				? propsBtn.iconLeftSvgMarkup.trim()
				: "";
		const rawRight =
			typeof propsBtn.iconRightSvgMarkup === "string"
				? propsBtn.iconRightSvgMarkup.trim()
				: "";
		const safeLeft = rawLeft ? sanitizeIconifySvgMarkup(rawLeft) : null;
		const safeRight = rawRight ? sanitizeIconifySvgMarkup(rawRight) : null;
		const isLucide = propsBtn.buttonIconKind === "lucide_animated";
		const useRightSlot = propsBtn.iconPlacement === "right";
		const lucidePxRaw = propsBtn.iconLucideSizePx;
		const lucidePx =
			typeof lucidePxRaw === "number" &&
			Number.isFinite(lucidePxRaw) &&
			lucidePxRaw > 0
				? Math.max(12, Math.min(128, Math.round(lucidePxRaw)))
				: 20;
		const ariaLeft =
			typeof propsBtn.iconLeftAriaLabel === "string"
				? propsBtn.iconLeftAriaLabel.trim()
				: "";
		const ariaRight =
			typeof propsBtn.iconRightAriaLabel === "string"
				? propsBtn.iconRightAriaLabel.trim()
				: "";
		const a11yLeft =
			ariaLeft.length > 0
				? ({ role: "img" as const, "aria-label": ariaLeft } as const)
				: ({ "aria-hidden": true as const } as const);
		const a11yRight =
			ariaRight.length > 0
				? ({ role: "img" as const, "aria-label": ariaRight } as const)
				: ({ "aria-hidden": true as const } as const);
		const iconSpanStyle: React.CSSProperties = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
			width: "1em",
			height: "1em",
			lineHeight: 0,
		};
		const lucideAria =
			typeof propsBtn.iconLucideAriaLabel === "string"
				? propsBtn.iconLucideAriaLabel.trim()
				: "";
		const a11yLucide =
			lucideAria.length > 0
				? ({ role: "img" as const, "aria-label": lucideAria } as const)
				: ({ "aria-hidden": true as const } as const);
		const lucideWrapperStyle: React.CSSProperties = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			lineHeight: 0,
			flexShrink: 0,
			width: lucidePx,
			height: lucidePx,
		};
		let lucideSubtree: React.ReactNode = null;
		if (isLucide) {
			const mode = propsBtn.iconLucideMode === "local" ? "local" : "library";
			const primary = resolveLucideAnimatedPrimaryColor(
				propsBtn.iconLucidePrimaryColor,
				style.color
			);
			const secondary = resolveLucideAnimatedSecondaryColor(
				propsBtn.iconLucideSecondaryColor
			);
			const strokeW = resolveLucideAnimatedStrokeWidth(
				propsBtn.iconLucideStrokeWidth
			);
			const plate: React.CSSProperties | undefined = secondary
				? {
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						maxWidth: "100%",
						maxHeight: "100%",
						boxSizing: "border-box",
						borderRadius: 9999,
						padding: 4,
						backgroundColor: secondary,
					}
				: undefined;
			let lucideInner: React.ReactNode;
			if (mode === "local") {
				const raw =
					typeof propsBtn.iconLucideLocalSvgMarkup === "string"
						? propsBtn.iconLucideLocalSvgMarkup.trim()
						: "";
				const safe = raw ? sanitizeLocalAnimatedSvgMarkup(raw) : null;
				lucideInner = safe ? (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: lucidePx,
							height: lucidePx,
						}}
					>
						{secondary ? (
							<span style={plate}>
								<span
									dangerouslySetInnerHTML={{ __html: safe }}
									style={{
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										width: "100%",
										height: "100%",
										color: primary,
										lineHeight: 0,
									}}
								/>
							</span>
						) : (
							<span
								dangerouslySetInnerHTML={{ __html: safe }}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									width: lucidePx,
									height: lucidePx,
									color: primary,
									lineHeight: 0,
								}}
							/>
						)}
					</span>
				) : (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							minWidth: 24,
							minHeight: 24,
							border: "1px dashed rgba(148,163,184,0.9)",
							borderRadius: 6,
							fontSize: 10,
							opacity: 0.65,
						}}
					>
						Icon
					</span>
				);
			} else {
				const iconId =
					typeof propsBtn.iconLucideLibraryId === "string" &&
					propsBtn.iconLucideLibraryId.trim()
						? propsBtn.iconLucideLibraryId.trim()
						: "SparklesIcon";
				const iconSvg = renderLucideAnimatedSsrFallback({
					iconId,
					sizePx: lucidePx,
					color: primary,
					strokeWidth: strokeW,
				});
				const hostClean = sanitizeAnimationHostDomId(
					propsBtn.iconLucideAnimationHostDomId
				);
				const playResolved = resolveLucideAnimatedPlayOn(
					propsBtn.iconLucidePlayOn
				);
				lucideInner = (
					<span
						data-pb-lucide-color={primary}
						{...(hostClean
							? {}
							: ({ "data-pb-lucide-delegate-button": "1" } as const))}
						data-pb-lucide-icon-id={iconId}
						data-pb-lucide-live=""
						data-pb-lucide-play={playResolved}
						data-pb-lucide-size={String(lucidePx)}
						data-pb-lucide-stroke={String(strokeW)}
						{...(hostClean
							? ({ "data-pb-lucide-animation-host": hostClean } as const)
							: {})}
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: lucidePx,
							height: lucidePx,
							color: primary,
						}}
					>
						{secondary ? <span style={plate}>{iconSvg}</span> : iconSvg}
					</span>
				);
			}
			lucideSubtree = (
				<span {...a11yLucide} style={lucideWrapperStyle}>
					{lucideInner}
				</span>
			);
		}
		const hasIcons = Boolean(safeLeft || safeRight || isLucide);
		const inner = isLucide ? (
			<>
				{useRightSlot ? null : lucideSubtree}
				{label || "Button"}
				{useRightSlot ? lucideSubtree : null}
			</>
		) : (
			<>
				{safeLeft ? (
					<span
						{...a11yLeft}
						dangerouslySetInnerHTML={{ __html: safeLeft }}
						style={iconSpanStyle}
					/>
				) : null}
				{label || "Button"}
				{safeRight ? (
					<span
						{...a11yRight}
						dangerouslySetInnerHTML={{ __html: safeRight }}
						style={iconSpanStyle}
					/>
				) : null}
			</>
		);
		const baseStyle: React.CSSProperties = {
			...style,
			display: style.display || "inline-flex",
			justifyContent: alignment ? justifyMap[alignment] : undefined,
			alignItems: "center",
			...(hasIcons ? { gap: "0.35em" } : {}),
		};
		const lightboxDom = isLightboxTrigger
			? {
					...dom,
					"data-lightbox-id": openLightboxId,
					"data-lightbox-trigger": "click",
				}
			: dom;
		// No onClick: this renders in a Server Component; the document-level CTA listener tracks the click.
		const button = isLink ? (
			<a
				{...dom}
				className={[BUILDER_BUTTON_LINK_CLASS, dom.className]
					.filter(Boolean)
					.join(" ")}
				href={href}
				key={node.id}
				rel={openInNewTab ? "noopener noreferrer" : undefined}
				style={{ ...baseStyle, textDecoration: "none" }}
				target={openInNewTab ? "_blank" : undefined}
			>
				{inner}
			</a>
		) : (
			<button {...lightboxDom} key={node.id} style={baseStyle} type="button">
				{inner}
			</button>
		);
		return wrap ? wrap(node, button) : button;
	}

	if (node.type === "iconify_icon") {
		const p = (node.props ?? {}) as {
			iconId?: string;
			svgMarkup?: string;
			ariaLabel?: string;
			href?: string;
			linkTarget?: "_blank" | "_self";
		};
		const rawSvg = typeof p.svgMarkup === "string" ? p.svgMarkup.trim() : "";
		const safe = rawSvg ? sanitizeIconifySvgMarkup(rawSvg) : null;
		const aria = typeof p.ariaLabel === "string" ? p.ariaLabel.trim() : "";
		const href = trimmedElementLinkHref(p.href);
		// When the icon is a link, its accessible name must live on the <a> and
		// the icon stays decorative. Never emit a nameless link: fall back to a
		// label derived from the destination when the author left one blank.
		const linkAria =
			href == null ? "" : aria || deriveIconLinkAccessibleName(href);
		const decorativeA11y = { "aria-hidden": true as const } as const;
		const a11y =
			href == null && aria.length > 0
				? ({ role: "img" as const, "aria-label": aria } as const)
				: decorativeA11y;
		const wrapperStyle: React.CSSProperties = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			lineHeight: 0,
			flexShrink: 0,
			overflow: "visible",
			boxSizing: "border-box",
			...style,
		};
		const inner = safe ? (
			<span
				{...a11y}
				dangerouslySetInnerHTML={{ __html: safe }}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					boxSizing: "border-box",
					width: "100%",
					height: "100%",
					maxWidth: "100%",
					maxHeight: "100%",
				}}
			/>
		) : (
			<span
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					minWidth: 24,
					minHeight: 24,
					border: "1px dashed rgba(148,163,184,0.9)",
					borderRadius: 6,
					fontSize: 10,
					padding: "4px 6px",
					opacity: 0.65,
				}}
			>
				Icon
			</span>
		);
		const openInNewTab = elementLinkOpensInNewTab(p.linkTarget);
		const content =
			href == null ? (
				<span {...dom} key={node.id} style={wrapperStyle}>
					{inner}
				</span>
			) : (
				<a
					{...dom}
					aria-label={linkAria}
					href={href}
					key={node.id}
					rel={openInNewTab ? "noopener noreferrer" : undefined}
					style={{
						...publishedLinkedElementAnchorStyle,
						...wrapperStyle,
					}}
					target={openInNewTab ? "_blank" : undefined}
				>
					{inner}
				</a>
			);
		return wrap ? wrap(node, content) : content;
	}

	if (node.type === "lordicon_icon") {
		const p = (node.props ?? {}) as {
			lordiconSrc?: string;
			trigger?: string;
			primaryColor?: string;
			secondaryColor?: string;
			ariaLabel?: string;
		};
		const rawSrc =
			typeof p.lordiconSrc === "string" ? p.lordiconSrc.trim() : "";
		const src =
			sanitizeLordiconSrc(rawSrc) ??
			sanitizeLordiconSrc(DEFAULT_LORDICON_JSON_SRC);
		const trigger = sanitizeLordiconTrigger(p.trigger);
		const colors = formatLordiconColors(p.primaryColor, p.secondaryColor);
		const aria = typeof p.ariaLabel === "string" ? p.ariaLabel.trim() : "";
		const w = parsePxInline(style.width, 48);
		const h = parsePxInline(style.height, 48);
		const wrapperStyle: React.CSSProperties = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			lineHeight: 0,
			flexShrink: 0,
			overflow: "visible",
			boxSizing: "border-box",
			...style,
		};
		const lordProps: Record<string, unknown> = {
			src,
			trigger,
			style: {
				width: w,
				height: h,
				display: "inline-block",
				flexShrink: 0,
				boxSizing: "border-box",
			},
		};
		if (colors) {
			lordProps.colors = colors;
		}
		if (aria.length > 0) {
			lordProps["aria-label"] = aria;
		} else {
			lordProps["aria-hidden"] = true;
		}
		const inner = React.createElement("lord-icon", lordProps);
		const content = (
			<span {...dom} key={node.id} style={wrapperStyle}>
				{inner}
			</span>
		);
		return wrap ? wrap(node, content) : content;
	}

	if (node.type === "lucide_animated_icon") {
		const p = (node.props ?? {}) as {
			mode?: string;
			libraryIconId?: string;
			localSvgMarkup?: string;
			ariaLabel?: string;
			primaryColor?: string;
			secondaryColor?: string;
			strokeWidth?: string;
			playOn?: string;
			animationHostDomId?: string;
		};
		const mode = p.mode === "local" ? "local" : "library";
		const aria = typeof p.ariaLabel === "string" ? p.ariaLabel.trim() : "";
		const w = parsePxInline(style.width, 40);
		const h = parsePxInline(style.height, 40);
		const px = Math.max(12, Math.min(w, h));
		const primary = resolveLucideAnimatedPrimaryColor(
			p.primaryColor,
			style.color
		);
		const secondary = resolveLucideAnimatedSecondaryColor(p.secondaryColor);
		const strokeW = resolveLucideAnimatedStrokeWidth(p.strokeWidth);
		const wrapperStyle: React.CSSProperties = {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			lineHeight: 0,
			flexShrink: 0,
			overflow: "visible",
			boxSizing: "border-box",
			...style,
		};
		const a11y =
			aria.length > 0
				? ({ role: "img" as const, "aria-label": aria } as const)
				: ({ "aria-hidden": true as const } as const);

		const plate: React.CSSProperties | undefined = secondary
			? {
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					maxWidth: "100%",
					maxHeight: "100%",
					boxSizing: "border-box",
					borderRadius: 9999,
					padding: 4,
					backgroundColor: secondary,
				}
			: undefined;

		let inner: React.ReactNode;
		if (mode === "local") {
			const raw =
				typeof p.localSvgMarkup === "string" ? p.localSvgMarkup.trim() : "";
			const safe = raw ? sanitizeLocalAnimatedSvgMarkup(raw) : null;
			inner = safe ? (
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: px,
						height: px,
					}}
				>
					{secondary ? (
						<span style={plate}>
							<span
								dangerouslySetInnerHTML={{ __html: safe }}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									width: "100%",
									height: "100%",
									color: primary,
									lineHeight: 0,
								}}
							/>
						</span>
					) : (
						<span
							dangerouslySetInnerHTML={{ __html: safe }}
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: px,
								height: px,
								color: primary,
								lineHeight: 0,
							}}
						/>
					)}
				</span>
			) : (
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: 24,
						minHeight: 24,
						border: "1px dashed rgba(148,163,184,0.9)",
						borderRadius: 6,
						fontSize: 10,
						opacity: 0.65,
					}}
				>
					Icon
				</span>
			);
		} else {
			const iconId =
				typeof p.libraryIconId === "string" && p.libraryIconId.trim()
					? p.libraryIconId.trim()
					: "SparklesIcon";
			const iconSvg = renderLucideAnimatedSsrFallback({
				iconId,
				sizePx: px,
				color: primary,
				strokeWidth: strokeW,
			});
			const hostClean = sanitizeAnimationHostDomId(p.animationHostDomId);
			const playResolved = resolveLucideAnimatedPlayOn(p.playOn);
			inner = (
				<span
					data-pb-lucide-color={primary}
					data-pb-lucide-icon-id={iconId}
					data-pb-lucide-live=""
					data-pb-lucide-play={playResolved}
					data-pb-lucide-size={String(px)}
					data-pb-lucide-stroke={String(strokeW)}
					{...(hostClean
						? ({ "data-pb-lucide-animation-host": hostClean } as const)
						: {})}
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: px,
						height: px,
						color: primary,
					}}
				>
					{secondary ? <span style={plate}>{iconSvg}</span> : iconSvg}
				</span>
			);
		}

		const content = (
			<span {...dom} {...a11y} key={node.id} style={wrapperStyle}>
				{inner}
			</span>
		);
		return wrap ? wrap(node, content) : content;
	}

	if (node.type === "image") {
		const { propsImg, topImg } = parseImageNodeProps(node);
		const { binding, imageSrc, lazyPref, rawSrc } =
			resolveImageSrcFromImageProps(propsImg, topImg, options);
		const alt = propsImg.alt ?? topImg.alt;
		const title = propsImg.title ?? topImg.title;
		const hrefRaw = propsImg.href ?? topImg.link;
		const href = typeof hrefRaw === "string" ? hrefRaw.trim() : "";
		// A linked image with empty alt is a nameless link; label the <a> from
		// the destination so it is never invisible to screen readers or crawlers.
		const hasAltText = typeof alt === "string" && alt.trim().length > 0;
		const imageLinkFallbackAria = hasAltText
			? undefined
			: deriveIconLinkAccessibleName(href);
		const linkTarget = propsImg.linkTarget ?? topImg.linkTarget;
		const objectFit = propsImg.objectFit;
		const objectPosition = propsImg.objectPosition;
		const fit = pickResponsiveOrDirect(objectFit);
		const position = pickResponsiveOrDirect(objectPosition);
		const resolvedObjectFit =
			(typeof fit === "string" && fit.trim() ? fit : undefined) ??
			style.objectFit;
		const resolvedObjectPosition =
			(typeof position === "string" && position.trim()
				? position
				: undefined) ??
			(typeof style.objectPosition === "string" && style.objectPosition.trim()
				? style.objectPosition
				: undefined) ??
			"center";
		const effectiveHeight = resolveImageHeight(style.width, style.height);
		const suppressResponsiveSrcSet =
			options?.featuredImagePreview === true && binding !== "static";
		const responsiveSrcSet =
			suppressResponsiveSrcSet ||
			typeof propsImg.srcSet !== "string" ||
			!propsImg.srcSet.trim()
				? ""
				: propsImg.srcSet.trim();
		const slotCssPx =
			typeof propsImg.slotVariantCssPx === "number" &&
			Number.isFinite(propsImg.slotVariantCssPx) &&
			propsImg.slotVariantCssPx > 0
				? Math.round(propsImg.slotVariantCssPx)
				: null;
		const responsiveSizes =
			typeof propsImg.sizes === "string" && propsImg.sizes.trim()
				? propsImg.sizes.trim()
				: responsiveSrcSet
					? slotCssPx
						? `(max-width: 767px) 100vw, min(100vw, ${slotCssPx}px)`
						: imageSizesAttributeFromLayout({
								maxWidth: style.maxWidth,
								width: style.width,
							})
					: "";
		const capRaw = propsImg.caption ?? topImg.caption;
		const cap = typeof capRaw === "string" ? capRaw.trim() : "";
		const showCap = propsImg.showCaption === true && cap.length > 0;
		const isLcpImage =
			options?.prioritizeFirstContentImage === true &&
			options?.lcpImageNodeId === node.id &&
			lazyPref !== true;
		const loading = isLcpImage
			? "eager"
			: lazyPref === false
				? "eager"
				: "lazy";
		const fetchPriority = isLcpImage ? ("high" as const) : undefined;
		const openFull =
			propsImg.openFullSizeOnClick === true ||
			topImg.openFullSizeOnClick === true;
		const openFullHref = rawSrc || imageSrc;

		const hrefActive = isActiveHref(href);
		const domOnFigure = !hrefActive && showCap;
		const domOnImg = !(hrefActive || showCap);

		const intrinsicImgProps = resolvePublicImageIntrinsicProps({
			propsImg,
			imageSrc,
			binding,
		});

		const imageTitle =
			typeof title === "string" && title.trim() ? title : undefined;
		const staticMediaIdRaw = (propsImg.mediaId ?? topImg.mediaId)?.trim() ?? "";
		const staticPreviewMediaId =
			options?.featuredImagePreview === true &&
			binding === "static" &&
			IMAGE_MEDIA_UUID_RE.test(staticMediaIdRaw)
				? staticMediaIdRaw
				: "";
		const imageProps: React.ImgHTMLAttributes<HTMLImageElement> = {
			className: domOnImg ? dom.className : undefined,
			decoding: "async",
			fetchPriority,
			height: intrinsicImgProps.height,
			id: domOnImg ? dom.id : undefined,
			loading,
			width: intrinsicImgProps.width,
			...(responsiveSrcSet
				? { sizes: responsiveSizes, srcSet: responsiveSrcSet }
				: {}),
			src: imageSrc,
			style: {
				...style,
				width: "100%",
				minWidth: 0,
				objectFit: resolvedObjectFit as React.CSSProperties["objectFit"],
				objectPosition: resolvedObjectPosition,
				height: effectiveHeight ?? style.height ?? "auto",
			},
		};
		const imgEl = imageSrc ? (
			options?.featuredImagePreview === true && binding !== "static" ? (
				<DynamicPreviewImage
					{...imageProps}
					binding={binding}
					data-pb-node={node.id}
					elementAlt={alt || ""}
					elementTitle={imageTitle}
					key={`${node.id}-img`}
				/>
			) : staticPreviewMediaId ? (
				<StaticPreviewImage
					{...imageProps}
					data-pb-node={node.id}
					elementAlt={typeof alt === "string" ? alt : ""}
					elementTitle={typeof title === "string" ? title : undefined}
					key={`${node.id}-img`}
					mediaId={staticPreviewMediaId}
				/>
			) : (
				<img
					{...imageProps}
					alt={alt || ""}
					data-pb-node={node.id}
					key={`${node.id}-img`}
					title={imageTitle}
				/>
			)
		) : (
			<div
				{...dom}
				key={node.id}
				style={{
					border: "1px dashed currentColor",
					minHeight: 120,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 12,
					opacity: 0.8,
					...style,
				}}
			>
				Image
			</div>
		);

		const inner =
			showCap && imageSrc ? (
				<figure
					className={domOnFigure ? dom.className : undefined}
					id={domOnFigure ? dom.id : undefined}
					key={`${node.id}-fig`}
					style={{ margin: 0, display: "inline-block", maxWidth: "100%" }}
				>
					{imgEl}
					<figcaption
						style={{
							fontSize: "0.875em",
							marginTop: "0.35em",
							opacity: 0.92,
						}}
					>
						{cap}
					</figcaption>
				</figure>
			) : (
				imgEl
			);

		let wrappedImage: React.ReactNode = inner;
		if (hrefActive && imageSrc) {
			wrappedImage = (
				<a
					aria-label={imageLinkFallbackAria}
					className={dom.className}
					href={href}
					id={dom.id}
					key={`${node.id}-link`}
					rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
					style={{
						...publishedLinkedElementAnchorStyle,
						display: "inline-block",
						maxWidth: "100%",
					}}
					target={linkTarget}
				>
					{inner}
				</a>
			);
		} else if (openFull && openFullHref && !hrefActive) {
			wrappedImage = (
				<a
					aria-label={hasAltText ? undefined : "View full-size image"}
					href={openFullHref}
					key={`${node.id}-full`}
					rel="noopener noreferrer"
					style={{
						...publishedLinkedElementAnchorStyle,
						display: "inline-block",
						maxWidth: "100%",
					}}
					target="_blank"
				>
					{inner}
				</a>
			);
		}

		return wrap ? wrap(node, wrappedImage) : wrappedImage;
	}

	return null;
}
